import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import Collapse from '@mui/material/Collapse';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Paper from '@mui/material/Paper';
import Snackbar from '@mui/material/Snackbar';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import NewReleasesIcon from '@mui/icons-material/NewReleases';
import VerifiedIcon from '@mui/icons-material/Verified';
import CheckIcon from '@mui/icons-material/Check';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import CameraAltOutlinedIcon from '@mui/icons-material/CameraAltOutlined';
import CloseIcon from '@mui/icons-material/Close';
import PublicOutlinedIcon from '@mui/icons-material/PublicOutlined';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import { useTranslation } from '@eruvaka/web-pondlogs-i18n';
import Alert from '@mui/material/Alert';
import { colorTokens, AppInput, AppSelect } from '@eruvaka/web-pondlogs-base';
import { fetchUserProfile, updateUserProfileWithMessage, updatePassword, uploadProfilePhoto, } from '../../api/userApi';
import { fetchSubUsersGrouped, createSubUser, updateSubUser, updateSubUserWithMessage, updateSubUserPermissions, updateSubUserPassword, uploadSubUserPhoto, deleteSubUser, } from '../../api/subUsersApi';
import { fetchCountries, fetchTimezones, } from '../../api/countryTimezoneApi';
import { fetchLocations } from '../../api/locationApi';
import { useLocations } from '../../hooks/useLocations';
import OTPVerificationDialog from './OTPVerificationDialog';
const CREATE_SUB_ROLES = ['manager', 'technician'];
const EDIT_SUB_ROLES = ['owner', 'manager', 'worker', 'technician', 'no_access'];
const FARM_USER_GROUP_ORDER = ['owner', 'manager', 'worker', 'technician', 'no_access'];
const ROLE_LABELS = {
    admin: 'Admin',
    owner: 'Pond Owner',
    manager: 'Pond Manager',
    worker: 'Pond Worker',
    technician: 'Pond Technician',
    no_access: 'No Access',
    account_manager: 'Account Manager',
    ADMIN: 'Admin',
    POND_OWNER: 'Pond Owner',
    POND_MANAGER: 'Pond Manager',
    POND_WORKER: 'Pond Worker',
    POND_TECHNICIAN: 'Pond Technician',
    NO_ACCESS: 'No Access',
};
const MODULE_DEFS = [
    { key: 'schedules', label: 'Schedules Management', i18nKey: 'moduleSchedules' },
    { key: 'devices', label: 'Device Management', i18nKey: 'moduleDevices' },
    { key: 'sampling', label: 'Manage Sampling', i18nKey: 'moduleSampling' },
    { key: 'harvest', label: 'Harvest Management', i18nKey: 'moduleHarvest' },
    { key: 'inputs', label: 'Manage Inputs', i18nKey: 'moduleInputs' },
    { key: 'labTests', label: 'Manage Lab Tests', i18nKey: 'moduleLabTests' },
    { key: 'inventory', label: 'Manage Inventory', i18nKey: 'moduleInventory' },
];
const PERMS = ['view', 'update', 'delete'];
const PW_RULES = [
    { key: 'pwRule8Chars', test: (v) => v.length >= 8 },
    { key: 'pwRule1Upper', test: (v) => /[A-Z]/.test(v) },
    { key: 'pwRule1Lower', test: (v) => /[a-z]/.test(v) },
    { key: 'pwRule1Number', test: (v) => /\d/.test(v) },
    {
        key: 'pwRule1Special',
        test: (v) => /[!@#$%^&*()\-_=+[\]{};':",.<>/?`~\\|]/.test(v),
    },
];
const initials = (first, last) => `${first?.[0] ?? ''}${last?.[0] ?? ''}`.toUpperCase();
const roleLabel = (role) => ROLE_LABELS[role] ?? role.replace(/_/g, ' ');
const getModulePermissions = (modules, moduleKey) => {
    const mod = modules?.find((m) => m.moduleKey === moduleKey);
    if (!mod)
        return [];
    return PERMS.filter((p) => mod.permissions[p]);
};
const rolesWithLocationScope = ['manager', 'worker', 'technician'];
const rolesWithModuleScope = ['worker', 'technician'];
const requiresLocations = (role) => rolesWithLocationScope.includes(role);
const requiresModules = (role) => rolesWithModuleScope.includes(role);
const hasAnyModulePermission = (modules = []) => modules.some((module) => PERMS.some((perm) => module.permissions[perm]));
const applyViewDependency = (permissions, perm) => {
    const nextPermissions = {
        ...permissions,
        [perm]: !permissions[perm],
    };
    if ((perm === 'update' || perm === 'delete') && nextPermissions[perm]) {
        nextPermissions.view = true;
    }
    if (perm === 'view' && !nextPermissions.view) {
        nextPermissions.update = false;
        nextPermissions.delete = false;
    }
    return nextPermissions;
};
const Avatar = ({ first, last, gradient, size = 40, src, }) => src ? (_jsx(Box, { component: "img", src: src, alt: initials(first, last), sx: {
        width: size,
        height: size,
        borderRadius: '50%',
        objectFit: 'cover',
        flexShrink: 0,
    } })) : (_jsx(Box, { sx: {
        width: size,
        height: size,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: gradient,
        color: 'white',
        fontSize: size * 0.3,
        fontWeight: 700,
        flexShrink: 0,
    }, children: initials(first, last) }));
const RoleChip = ({ role }) => {
    const colors = {
        admin: { bg: `${colorTokens.orange}18`, color: colorTokens.orange },
        owner: { bg: `${colorTokens.magenta}15`, color: colorTokens.magenta },
        manager: { bg: `${colorTokens.primary}15`, color: colorTokens.primary },
        worker: { bg: `${colorTokens.secondary}18`, color: colorTokens.secondary },
        technician: { bg: `${colorTokens.secondary}15`, color: colorTokens.secondary },
        no_access: { bg: `${colorTokens.darkGrey}16`, color: colorTokens.darkGrey },
        account_manager: { bg: `${colorTokens.secondary}15`, color: colorTokens.secondary },
        pond_owner: { bg: `${colorTokens.magenta}15`, color: colorTokens.magenta },
        pond_manager: { bg: `${colorTokens.primary}15`, color: colorTokens.primary },
        pond_worker: { bg: `${colorTokens.secondary}18`, color: colorTokens.secondary },
        pond_technician: { bg: `${colorTokens.secondary}15`, color: colorTokens.secondary },
    };
    const s = colors[role.toLowerCase()] ?? {
        bg: `${colorTokens.darkGrey}12`,
        color: colorTokens.darkGrey,
    };
    return (_jsx(Box, { component: "span", sx: {
            px: 0.75,
            py: 0.25,
            borderRadius: 0.75,
            fontSize: 10,
            fontWeight: 700,
            background: s.bg,
            color: s.color,
        }, children: roleLabel(role) }));
};
const PwChecklist = ({ value }) => {
    const { t } = useTranslation('main');
    return (_jsx(Box, { sx: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.5, mt: 1 }, children: PW_RULES.map((rule) => {
            const pass = rule.test(value);
            return (_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 0.75 }, children: [_jsx(Box, { sx: {
                            width: 14,
                            height: 14,
                            borderRadius: '50%',
                            flexShrink: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: pass ? colorTokens.accent : `${colorTokens.error}20`,
                        }, children: pass ? (_jsx(CheckIcon, { sx: { fontSize: 8, color: 'white', strokeWidth: 3 } })) : (_jsx(Box, { sx: { width: 6, height: 1.5, bgcolor: colorTokens.error, borderRadius: 1 } })) }), _jsx(Typography, { sx: { fontSize: 9, color: pass ? '#5a6e00' : colorTokens.error }, children: t(`profileUsers.${rule.key}`) })] }, rule.key));
        }) }));
};
export const ProfileUsers = memo((props) => {
    const { onProfilePhotoUpdated } = props;
    const { t } = useTranslation('main');
    const { selectedLocation } = useLocations();
    const [selfProfile, setSelfProfile] = useState(null);
    const [subUsers, setSubUsers] = useState([]);
    const [skrettingTechnicians, setSkrettingTechnicians] = useState([]);
    const [loadingProfile, setLoadingProfile] = useState(true);
    const [loadingUsers, setLoadingUsers] = useState(true);
    const [loadingCountries, setLoadingCountries] = useState(true);
    const [loadingTimezones, setLoadingTimezones] = useState(false);
    const [globalError, setGlobalError] = useState(null);
    const timezoneReqRef = useRef(0);
    const [search, setSearch] = useState('');
    const [selected, setSelected] = useState('self');
    const [countries, setCountries] = useState([]);
    const [locations, setLocations] = useState([]);
    const [timezones, setTimezones] = useState([]);
    // Edit state for selected user
    const [editState, setEditState] = useState({
        firstName: '',
        lastName: '',
        countryCode: '',
        timezoneName: '',
        role: 'technician',
        assignedModules: [],
        assignedLocations: [],
    });
    // Profile save
    const [profileSaving, setProfileSaving] = useState(false);
    const [profileSaved, setProfileSaved] = useState(false);
    const [profileError, setProfileError] = useState(null);
    const [profileErrorSeverity, setProfileErrorSeverity] = useState('error');
    const [profileSaveState, setProfileSaveState] = useState('idle');
    const [saveToast, setSaveToast] = useState({
        open: false,
        message: '',
        severity: 'success',
    });
    // Permissions save
    const [permSaving, setPermSaving] = useState(false);
    const [permSaved, setPermSaved] = useState(false);
    const [permError, setPermError] = useState(null);
    const [permSaveState, setPermSaveState] = useState('idle');
    // Password
    const [pw, setPw] = useState({ current: '', next: '', confirm: '' });
    const [showPw, setShowPw] = useState({ current: false, next: false, confirm: false });
    const [pwSaving, setPwSaving] = useState(false);
    const [pwSaved, setPwSaved] = useState(false);
    const [pwError, setPwError] = useState(null);
    // Module accordion
    // const [openModules, setOpenModules] = useState<string[]>([])
    // Add user inline form
    const [isAddMode, setIsAddMode] = useState(false);
    const [addPayload, setAddPayload] = useState({
        role: 'technician',
        assignedModules: [],
        assignedLocations: [],
    });
    const [addSaving, setAddSaving] = useState(false);
    const [addError, setAddError] = useState(null);
    const [addFieldErrors, setAddFieldErrors] = useState({});
    // Delete confirmation
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleteText, setDeleteText] = useState('');
    const [deleteLoading, setDeleteLoading] = useState(false);
    // Status toggle confirmation
    const [statusTarget, setStatusTarget] = useState(null);
    const [statusLoading, setStatusLoading] = useState(false);
    // Reset link (for sub-user password reset)
    // const [resetLinkLoading, setResetLinkLoading] = useState(false)
    // const [resetLinkResult, setResetLinkResult] = useState<'success' | 'error' | null>(null)
    // Avatar upload
    // Avatar upload (self)
    const avatarInputRef = useRef(null);
    const [avatarUploading, setAvatarUploading] = useState(false);
    const [avatarError, setAvatarError] = useState(null);
    const [avatarSuccess, setAvatarSuccess] = useState(false);
    const [avatarErrorSeverity, setAvatarErrorSeverity] = useState('error');
    const isSelf = selected === 'self';
    const selectedUser = isSelf ? null : selected;
    const displayEmail = isSelf ? (selfProfile?.email ?? '') : (selectedUser?.email ?? '');
    // Avatar upload (add-user form)
    const addAvatarInputRef = useRef(null);
    const [addAvatarPreview, setAddAvatarPreview] = useState(null);
    const [addAvatarFile, setAddAvatarFile] = useState(null);
    const [addAvatarUploading, setAddAvatarUploading] = useState(false);
    // OTP dialog for email/phone change
    const [otpDialogOpen, setOtpDialogOpen] = useState(false);
    const [otpDialogType, setOtpDialogType] = useState('email');
    const [otpDialogUserId, setOtpDialogUserId] = useState(undefined);
    const [otpCurrentValue, setOtpCurrentValue] = useState('');
    const [otpIsIndia, setOtpIsIndia] = useState(false);
    const openOTPDialog = (type, userId) => {
        setOtpDialogType(type);
        setOtpDialogUserId(userId);
        // Compute current value and India flag at open time
        if (userId) {
            const allUsers = [...subUsers, ...skrettingTechnicians];
            const u = allUsers.find((s) => s.id === userId) ?? selected;
            setOtpCurrentValue(type === 'email' ? (u?.email ?? '') : (u?.phone ?? ''));
            setOtpIsIndia(u?.country?.code === 'IN');
        }
        else {
            setOtpCurrentValue(type === 'email'
                ? (selfProfile?.email ?? '')
                : (selfProfile?.phoneNumber ??
                    selfProfile?.mobile ??
                    ''));
            setOtpIsIndia(selfProfile?.country?.code === 'IN');
        }
        setOtpDialogOpen(true);
    };
    const handleOTPSuccess = (newValue) => {
        if (!otpDialogUserId) {
            // Self — update local selfProfile display
            setSelfProfile((prev) => prev
                ? {
                    ...prev,
                    ...(otpDialogType === 'email' ? { email: newValue } : { phoneNumber: newValue }),
                }
                : prev);
        }
        else {
            // Sub-user — update in subUsers list
            setSubUsers((prev) => prev.map((u) => u.id === otpDialogUserId
                ? { ...u, ...(otpDialogType === 'email' ? { email: newValue } : { phone: newValue }) }
                : u));
            // Also update editState if this user is currently selected
            if (selected === otpDialogUserId) {
                setEditState((prev) => otpDialogType === 'email' ? { ...prev, email: newValue } : { ...prev, phone: newValue });
            }
        }
    };
    const resolveAvatarError = (err) => {
        if (!(err instanceof Error) || !err.message.trim()) {
            return { message: t('profileUsers.avatarUploadError'), severity: 'warning' };
        }
        const normalized = err.message.toLowerCase();
        const isServerFailure = normalized.includes('internal server error') ||
            normalized.includes('the specified bucket does not exist') ||
            normalized.includes('bucket') ||
            normalized.includes('networkerror') ||
            normalized.includes('failed to fetch');
        if (isServerFailure) {
            return { message: t('profileUsers.avatarUploadError'), severity: 'warning' };
        }
        return { message: err.message, severity: 'error' };
    };
    const handleAvatarClick = () => {
        avatarInputRef.current?.click();
    };
    // const handleSendResetLink = async () => {
    //   if (!displayEmail) return
    //   setResetLinkLoading(true)
    //   setResetLinkResult(null)
    //   try {
    // //     const response = await fetch(endpoint(env.apiBaseUrl, env.forgotPasswordPath), {
    //       method: 'POST',
    //       headers: { 'Content-Type': 'application/json' },
    //       body: JSON.stringify({ username: displayEmail, language: i18n.language }),
    //     })
    //     if (!response.ok) throw new Error(`status ${response.status}`)
    //     setResetLinkResult('success')
    //     setTimeout(() => setResetLinkResult(null), 4000)
    //   } catch {
    //     setResetLinkResult('error')
    //     setTimeout(() => setResetLinkResult(null), 4000)
    //   } finally {
    //     setResetLinkLoading(false)
    //   }
    // }
    const handleAddAvatarChange = (e) => {
        const file = e.target.files?.[0];
        if (!file)
            return;
        e.target.value = '';
        if (addAvatarPreview)
            URL.revokeObjectURL(addAvatarPreview);
        setAddAvatarPreview(URL.createObjectURL(file));
        setAddAvatarFile(file);
    };
    const getTranslatedSaveSuccessMessage = (apiMessage) => {
        const normalized = (apiMessage ?? '').toLowerCase().trim();
        if (normalized.includes('profile updated successfully')) {
            return t('profileUsers.toastProfileUpdated');
        }
        if (normalized.includes('user updated successfully')) {
            return t('profileUsers.toastUserUpdated');
        }
        return t('profileUsers.toastSavedDefault');
    };
    const getTranslatedSaveFailureMessage = (apiMessage) => {
        const normalized = apiMessage.toLowerCase().trim();
        const isValidationWarning = normalized.includes('assigned locations are invalid') ||
            normalized.includes('assigned locations are required') ||
            normalized.includes('module permissions are required');
        if (isValidationWarning) {
            return {
                severity: 'warning',
                message: t('profileUsers.toastSaveWarning'),
            };
        }
        return {
            severity: 'error',
            message: t('profileUsers.toastSaveFailed'),
        };
    };
    const handleAvatarChange = (e) => {
        const file = e.target.files?.[0];
        if (!file)
            return;
        // Reset so same file can be re-selected after an error
        e.target.value = '';
        setAvatarSuccess(false);
        setAvatarError(null);
        setAvatarErrorSeverity('error');
        setAvatarUploading(true);
        uploadProfilePhoto(file)
            .then((updated) => {
            setSelfProfile(updated);
            if (updated.profilePhotoUrl) {
                onProfilePhotoUpdated?.(updated.profilePhotoUrl);
            }
            setAvatarSuccess(true);
            setTimeout(() => setAvatarSuccess(false), 2500);
        })
            .catch((err) => {
            const feedback = resolveAvatarError(err);
            setAvatarSuccess(false);
            setAvatarError(feedback.message);
            setAvatarErrorSeverity(feedback.severity);
        })
            .finally(() => setAvatarUploading(false));
    };
    useEffect(() => {
        fetchUserProfile()
            .then((p) => {
            setSelfProfile(p);
            setSelected('self');
            setEditState({
                firstName: p.firstName ?? p.first_name ?? '',
                lastName: p.lastName ?? p.last_name ?? '',
                countryCode: p.country?.code ?? '',
                timezoneName: p.timezone?.name ?? '',
                role: 'owner',
                assignedModules: [],
                assignedLocations: [],
            });
        })
            .catch((err) => setGlobalError(err instanceof Error ? err.message : 'Failed to load profile'))
            .finally(() => setLoadingProfile(false));
    }, []);
    useEffect(() => {
        setLoadingUsers(true);
        fetchSubUsersGrouped(selectedLocation?.locationId)
            .then((result) => {
            setSubUsers(result.subUsers);
            setSkrettingTechnicians(result.skrettingTechnicians);
        })
            .catch(() => { })
            .finally(() => setLoadingUsers(false));
    }, [selectedLocation?.locationId]);
    useEffect(() => {
        let isMounted = true;
        setLoadingCountries(true);
        fetchCountries()
            .then((list) => {
            if (!isMounted)
                return;
            setCountries(list);
        })
            .catch((err) => {
            if (!isMounted)
                return;
            setGlobalError(err instanceof Error ? err.message : 'Failed to load countries');
        })
            .finally(() => {
            if (isMounted)
                setLoadingCountries(false);
        });
        return () => {
            isMounted = false;
        };
    }, []);
    useEffect(() => {
        let isMounted = true;
        fetchLocations()
            .then((list) => {
            if (!isMounted)
                return;
            setLocations(list);
        })
            .catch(() => {
            if (!isMounted)
                return;
            // Locations panel is optional context; keep silent on failure.
        });
        return () => {
            isMounted = false;
        };
    }, []);
    const resolveCountry = useCallback((inputCountryCode) => {
        const normalizedCode = inputCountryCode.trim().toLowerCase();
        if (!normalizedCode)
            return countries[0] ?? null;
        return (countries.find((country) => country.code.trim().toLowerCase() === normalizedCode) ??
            countries[0] ??
            null);
    }, [countries]);
    const syncTimezonesForCountry = useCallback(async ({ countryInput, currentTimezone, preserveExistingTimezone, }) => {
        const country = resolveCountry(countryInput);
        if (!country) {
            setTimezones([]);
            setEditState((prev) => ({ ...prev, countryCode: '', timezoneName: '' }));
            return;
        }
        const requestId = ++timezoneReqRef.current;
        setLoadingTimezones(true);
        try {
            const timezoneList = await fetchTimezones(country.code);
            if (requestId !== timezoneReqRef.current)
                return;
            setTimezones(timezoneList);
            const hasCurrentTimezone = timezoneList.some((tz) => tz.name === currentTimezone);
            const resolvedTimezone = preserveExistingTimezone && hasCurrentTimezone
                ? currentTimezone
                : (timezoneList[0]?.name ?? '');
            setEditState((prev) => ({
                ...prev,
                countryCode: country.code,
                timezoneName: resolvedTimezone,
            }));
        }
        catch (err) {
            if (requestId !== timezoneReqRef.current)
                return;
            setTimezones([]);
            setProfileError(err instanceof Error ? err.message : 'Failed to load timezones');
        }
        finally {
            if (requestId === timezoneReqRef.current)
                setLoadingTimezones(false);
        }
    }, [resolveCountry]);
    useEffect(() => {
        if (countries.length === 0)
            return;
        void syncTimezonesForCountry({
            countryInput: editState.countryCode,
            currentTimezone: editState.timezoneName,
            preserveExistingTimezone: true,
        });
    }, [countries]);
    const q = search.toLowerCase();
    const filteredUsers = subUsers.filter((u) => !q ||
        u.fullName.toLowerCase().includes(q) ||
        u.email.toLowerCase().includes(q) ||
        roleLabel(u.role).toLowerCase().includes(q));
    const filteredSkretting = skrettingTechnicians.filter((u) => !q || u.fullName.toLowerCase().includes(q) || 'account manager'.includes(q));
    const showSelf = !q ||
        `${selfProfile?.firstName ?? selfProfile?.first_name ?? ''} ${selfProfile?.lastName ?? selfProfile?.last_name ?? ''}`
            .toLowerCase()
            .includes(q) ||
        roleLabel(selfProfile?.userType ?? selfProfile?.user_type ?? 'owner')
            .toLowerCase()
            .includes(q);
    const adminUsers = filteredUsers.filter((u) => u.role === 'admin');
    const groupedUsers = FARM_USER_GROUP_ORDER.map((role) => ({
        role,
        users: filteredUsers.filter((u) => u.role === role),
    })).filter((group) => group.users.length > 0);
    const pwValid = PW_RULES.every((r) => r.test(pw.next));
    const pwMatch = pw.next.length > 0 && pw.next === pw.confirm;
    const pwCanSubmit = pw.current.length > 0 && pwValid && pwMatch;
    const handleSelect = (entry) => {
        setIsAddMode(false);
        setAddError(null);
        setAddFieldErrors({});
        setSelected(entry);
        // setOpenModules([])
        setPw({ current: '', next: '', confirm: '' });
        setPwSaved(false);
        setProfileSaved(false);
        setProfileSaveState('idle');
        setProfileError(null);
        setPwError(null);
        setPermSaved(false);
        setPermSaveState('idle');
        setPermError(null);
        if (entry === 'self' && selfProfile) {
            const nextState = {
                firstName: selfProfile.firstName ?? selfProfile.first_name ?? '',
                lastName: selfProfile.lastName ?? selfProfile.last_name ?? '',
                countryCode: selfProfile.country?.code ?? '',
                timezoneName: selfProfile.timezone?.name ?? '',
                role: 'owner',
                assignedModules: [],
                assignedLocations: [],
            };
            setEditState(nextState);
            if (countries.length > 0) {
                void syncTimezonesForCountry({
                    countryInput: nextState.countryCode,
                    currentTimezone: nextState.timezoneName,
                    preserveExistingTimezone: true,
                });
            }
        }
        else if (entry !== 'self') {
            const u = entry;
            const adminCountryCode = selfProfile?.country?.code ?? '';
            const adminTimezoneName = selfProfile?.timezone?.name ?? '';
            const nextState = {
                firstName: u.firstName,
                lastName: u.lastName,
                countryCode: adminCountryCode || u.country?.code || '',
                timezoneName: adminTimezoneName || u.timezone?.name || '',
                role: u.role,
                assignedModules: u.assignedModules ?? [],
                assignedLocations: u.assignedLocations?.map((l) => l.id) ?? [],
            };
            setEditState(nextState);
            // Country/timezone are read-only for sub-users — no API call needed
        }
    };
    const handleCancelAdd = () => {
        setIsAddMode(false);
        setAddPayload({
            role: 'technician',
            assignedModules: [],
            assignedLocations: [],
            countryCode: 'IN',
        });
        setAddError(null);
        setAddFieldErrors({});
        // setOpenModules([])
        if (addAvatarPreview)
            URL.revokeObjectURL(addAvatarPreview);
        setAddAvatarPreview(null);
        setAddAvatarFile(null);
    };
    const handleSaveProfile = async () => {
        setProfileError(null);
        setProfileErrorSeverity('error');
        setProfileSaveState('idle');
        setProfileSaving(true);
        try {
            let apiSuccessMessage;
            if (isSelf) {
                const selectedCountry = resolveCountry(editState.countryCode);
                const selectedTimezone = timezones.find((tz) => tz.name === editState.timezoneName) ??
                    (editState.timezoneName ? { name: editState.timezoneName, offset: 0 } : null);
                const updated = await updateUserProfileWithMessage({
                    firstName: editState.firstName,
                    lastName: editState.lastName,
                    ...(selectedCountry
                        ? {
                            country: {
                                code: selectedCountry.code,
                                name: selectedCountry.name,
                            },
                        }
                        : {}),
                    ...(selectedTimezone
                        ? {
                            timezone: {
                                name: selectedTimezone.name,
                                offset: selectedTimezone.offset,
                            },
                        }
                        : {}),
                });
                setSelfProfile(updated.user);
                apiSuccessMessage = updated.message;
            }
            else if (selectedUser) {
                if (requiresLocations(editState.role) && editState.assignedLocations.length === 0) {
                    throw new Error(t('userFormDialog.errorLocationsRequired'));
                }
                const updated = await updateSubUserWithMessage(selectedUser.id, {
                    firstName: editState.firstName,
                    lastName: editState.lastName,
                    role: editState.role,
                    assignedLocations: requiresLocations(editState.role) ? editState.assignedLocations : [],
                });
                setSubUsers((prev) => prev.map((u) => (u.id === updated.user.id ? updated.user : u)));
                setSelected(updated.user);
                apiSuccessMessage = updated.message;
            }
            setProfileSaved(true);
            setProfileSaveState('success');
            setSaveToast({
                open: true,
                message: getTranslatedSaveSuccessMessage(apiSuccessMessage),
                severity: 'success',
            });
            setTimeout(() => setProfileSaved(false), 2500);
            setTimeout(() => setProfileSaveState('idle'), 2500);
        }
        catch (err) {
            const message = err instanceof Error ? err.message : 'Failed to save profile';
            const isLocationValidationIssue = /assigned locations are invalid/i.test(message);
            const failureToast = getTranslatedSaveFailureMessage(message);
            setProfileErrorSeverity(isLocationValidationIssue ? 'warning' : 'error');
            setProfileError(message);
            setSaveToast({
                open: true,
                message: failureToast.message,
                severity: failureToast.severity,
            });
            setProfileSaveState('error');
            setTimeout(() => setProfileSaveState('idle'), 2500);
        }
        finally {
            setProfileSaving(false);
        }
    };
    const handleSavePermissions = async () => {
        if (!selectedUser)
            return;
        if (!hasAnyModulePermission(editState.assignedModules)) {
            setPermError(`${t('profileUsers.modulePermissions')} ${t('profileUsers.noAccess')}`);
            return;
        }
        setPermError(null);
        setPermSaveState('idle');
        setPermSaving(true);
        try {
            const updated = await updateSubUserPermissions(selectedUser.id, editState.assignedModules);
            setSubUsers((prev) => prev.map((u) => (u.id === updated.id ? updated : u)));
            setSelected(updated);
            setPermSaved(true);
            setPermSaveState('success');
            setSaveToast({
                open: true,
                message: t('profileUsers.toastPermissionsUpdated'),
                severity: 'success',
            });
            setTimeout(() => setPermSaved(false), 2500);
            setTimeout(() => setPermSaveState('idle'), 2500);
        }
        catch (err) {
            const message = err instanceof Error ? err.message : 'Failed to save permissions';
            setPermError(message);
            setSaveToast({
                open: true,
                message: t('profileUsers.toastSaveFailed'),
                severity: 'error',
            });
            setPermSaveState('error');
            setTimeout(() => setPermSaveState('idle'), 2500);
        }
        finally {
            setPermSaving(false);
        }
    };
    const handlePasswordChange = async () => {
        setPwError(null);
        setPwSaving(true);
        try {
            if (isSelf) {
                await updatePassword({
                    currentPassword: pw.current,
                    newPassword: pw.next,
                    confirmPassword: pw.confirm,
                });
            }
            else if (selectedUser) {
                await updateSubUserPassword(selectedUser.id, pw.current, pw.next, pw.confirm);
            }
            setPwSaved(true);
            setPw({ current: '', next: '', confirm: '' });
            setTimeout(() => setPwSaved(false), 3000);
        }
        catch (err) {
            setPwError(err instanceof Error ? err.message : 'Failed to update password');
        }
        finally {
            setPwSaving(false);
        }
    };
    const toggleModulePerm = (moduleKey, perm) => {
        setEditState((prev) => {
            const existing = prev.assignedModules.find((m) => m.moduleKey === moduleKey);
            if (existing) {
                return {
                    ...prev,
                    assignedModules: prev.assignedModules.map((m) => m.moduleKey !== moduleKey
                        ? m
                        : {
                            ...m,
                            permissions: applyViewDependency(m.permissions, perm),
                        }),
                };
            }
            const modDef = MODULE_DEFS.find((d) => d.key === moduleKey);
            const newMod = {
                moduleKey,
                moduleName: modDef?.label ?? moduleKey,
                permissions: applyViewDependency({ view: false, update: false, delete: false }, perm),
            };
            return { ...prev, assignedModules: [...prev.assignedModules, newMod] };
        });
    };
    const handleEditRoleChange = (nextRole) => {
        setEditState((prev) => ({
            ...prev,
            role: nextRole,
            assignedLocations: requiresLocations(nextRole) ? prev.assignedLocations : [],
            assignedModules: requiresModules(nextRole) ? prev.assignedModules : [],
        }));
    };
    const toggleAddModulePerm = (moduleKey, perm) => {
        setAddPayload((prev) => {
            const currentModules = prev.assignedModules ?? [];
            const existing = currentModules.find((module) => module.moduleKey === moduleKey);
            if (existing) {
                return {
                    ...prev,
                    assignedModules: currentModules.map((module) => module.moduleKey !== moduleKey
                        ? module
                        : {
                            ...module,
                            permissions: applyViewDependency(module.permissions, perm),
                        }),
                };
            }
            const moduleDef = MODULE_DEFS.find((def) => def.key === moduleKey);
            const newModule = {
                moduleKey,
                moduleName: moduleDef?.label ?? moduleKey,
                permissions: applyViewDependency({ view: false, update: false, delete: false }, perm),
            };
            return {
                ...prev,
                assignedModules: [...currentModules, newModule],
            };
        });
    };
    const toggleColumnPerm = (perm) => {
        setEditState((prev) => {
            const allChecked = MODULE_DEFS.every((md) => {
                const m = prev.assignedModules.find((x) => x.moduleKey === md.key);
                return m?.permissions[perm] === true;
            });
            return {
                ...prev,
                assignedModules: MODULE_DEFS.map((md) => {
                    const existing = prev.assignedModules.find((x) => x.moduleKey === md.key);
                    const cur = existing?.permissions ?? { view: false, update: false, delete: false };
                    const updated = { ...cur };
                    if (allChecked) {
                        updated[perm] = false;
                        if (perm === 'view') {
                            updated.update = false;
                            updated.delete = false;
                        }
                    }
                    else {
                        updated[perm] = true;
                        if (perm === 'update' || perm === 'delete')
                            updated.view = true;
                    }
                    return { moduleKey: md.key, moduleName: md.label, permissions: updated };
                }),
            };
        });
    };
    const toggleAddColumnPerm = (perm) => {
        setAddPayload((prev) => {
            const currentModules = prev.assignedModules ?? [];
            const allChecked = MODULE_DEFS.every((md) => {
                const m = currentModules.find((x) => x.moduleKey === md.key);
                return m?.permissions[perm] === true;
            });
            return {
                ...prev,
                assignedModules: MODULE_DEFS.map((md) => {
                    const existing = currentModules.find((x) => x.moduleKey === md.key);
                    const cur = existing?.permissions ?? { view: false, update: false, delete: false };
                    const updated = { ...cur };
                    if (allChecked) {
                        updated[perm] = false;
                        if (perm === 'view') {
                            updated.update = false;
                            updated.delete = false;
                        }
                    }
                    else {
                        updated[perm] = true;
                        if (perm === 'update' || perm === 'delete')
                            updated.view = true;
                    }
                    return { moduleKey: md.key, moduleName: md.label, permissions: updated };
                }),
            };
        });
    };
    const handleAddRoleChange = (nextRole) => {
        setAddPayload((prev) => ({
            ...prev,
            role: nextRole,
            assignedLocations: requiresLocations(nextRole) ? (prev.assignedLocations ?? []) : [],
            assignedModules: requiresModules(nextRole) ? (prev.assignedModules ?? []) : [],
        }));
    };
    const handleAddUser = async () => {
        const role = (addPayload.role ?? 'technician');
        const newFieldErrors = {};
        if (!addPayload.firstName?.trim())
            newFieldErrors.firstName = t('profileUsers.errorFirstNameRequired');
        if (!addPayload.lastName?.trim())
            newFieldErrors.lastName = t('profileUsers.errorLastNameRequired');
        if (!addPayload.email?.trim()) {
            newFieldErrors.email = t('profileUsers.errorEmailRequired');
        }
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(addPayload.email)) {
            newFieldErrors.email = t('profileUsers.errorEmailInvalid');
        }
        if (!addPayload.phone?.trim())
            newFieldErrors.phone = t('profileUsers.errorPhoneRequired');
        if (Object.keys(newFieldErrors).length > 0) {
            setAddFieldErrors(newFieldErrors);
            return;
        }
        setAddFieldErrors({});
        if (requiresLocations(role) && (addPayload.assignedLocations?.length ?? 0) === 0) {
            setAddError(t('profileUsers.errorLocationsRequired'));
            return;
        }
        if (requiresModules(role) && !hasAnyModulePermission(addPayload.assignedModules ?? [])) {
            setAddError(t('profileUsers.errorModulePermissionsRequired'));
            return;
        }
        setAddError(null);
        setAddSaving(true);
        try {
            const created = await createSubUser({
                firstName: addPayload.firstName,
                lastName: addPayload.lastName,
                email: addPayload.email,
                phone: addPayload.phone,
                role,
                assignedModules: requiresModules(role) ? addPayload.assignedModules : [],
                assignedLocations: requiresLocations(role) ? addPayload.assignedLocations : [],
            });
            setSubUsers((prev) => [...prev, created]);
            // Upload photo if one was selected during add (best-effort, silent on failure)
            if (addAvatarFile) {
                setAddAvatarUploading(true);
                uploadSubUserPhoto(created.id, addAvatarFile)
                    .catch(() => {
                    /* silently ignore — sub-user photo upload is non-critical */
                })
                    .finally(() => setAddAvatarUploading(false));
            }
            setAddAvatarPreview(null);
            setAddAvatarFile(null);
            handleSelect(created);
            setSaveToast({
                open: true,
                message: t('profileUsers.toastUserCreated'),
                severity: 'success',
            });
        }
        catch (err) {
            setAddError(err instanceof Error ? err.message : 'Failed to create user');
        }
        finally {
            setAddSaving(false);
        }
    };
    const handleDeleteConfirm = async () => {
        if (!deleteTarget || deleteText !== 'Delete Account')
            return;
        setDeleteLoading(true);
        try {
            await deleteSubUser(deleteTarget.id);
            setSubUsers((prev) => prev.filter((u) => u.id !== deleteTarget.id));
            if (selected !== 'self' && selected.id === deleteTarget.id) {
                handleSelect('self');
            }
            setDeleteTarget(null);
            setDeleteText('');
        }
        catch (err) {
            setGlobalError(err instanceof Error ? err.message : 'Failed to delete user');
            setDeleteTarget(null);
        }
        finally {
            setDeleteLoading(false);
        }
    };
    const handleStatusConfirm = async () => {
        if (!statusTarget)
            return;
        setStatusLoading(true);
        try {
            const updated = await updateSubUser(statusTarget.id, {
                status: statusTarget.status === 'active' ? 'inactive' : 'active',
            });
            setSubUsers((prev) => prev.map((u) => (u.id === updated.id ? updated : u)));
            if (selected !== 'self' && selected.id === statusTarget.id) {
                setSelected(updated);
                setEditState((prev) => ({ ...prev }));
            }
            setStatusTarget(null);
        }
        catch (err) {
            setGlobalError(err instanceof Error ? err.message : 'Failed to update status');
            setStatusTarget(null);
        }
        finally {
            setStatusLoading(false);
        }
    };
    if (loadingProfile) {
        return (_jsx(Box, { sx: { display: 'flex', justifyContent: 'center', py: 6 }, children: _jsx(CircularProgress, { size: 28 }) }));
    }
    if (globalError) {
        return _jsx(Alert, { severity: "error", children: globalError });
    }
    const displayFirst = isSelf
        ? (selfProfile?.firstName ?? selfProfile?.first_name ?? '')
        : (selectedUser?.firstName ?? '');
    const displayLast = isSelf
        ? (selfProfile?.lastName ?? selfProfile?.last_name ?? '')
        : (selectedUser?.lastName ?? '');
    const displayRole = isSelf
        ? (selfProfile?.userType ?? selfProfile?.user_type ?? 'owner')
        : (selectedUser?.role ?? 'technician');
    const displayStatus = isSelf ? 'active' : (selectedUser?.status ?? 'active');
    const activeRole = isSelf ? 'owner' : editState.role;
    const isWorkerOrTechnician = !isSelf && requiresModules(activeRole);
    const isManager = !isSelf && activeRole === 'manager';
    const availableSubRoles = selectedUser ? EDIT_SUB_ROLES : CREATE_SUB_ROLES;
    return (_jsxs(Box, { sx: {
            display: 'flex',
            gap: 1.5,
            alignItems: 'flex-start',
            '& .MuiOutlinedInput-root': {
                borderRadius: '12px',
                backgroundColor: '#fff',
                '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: colorTokens.borderLight,
                    borderWidth: 1,
                },
            },
            '& .MuiInputBase-input, & .MuiSelect-select': {
                fontSize: 12,
            },
            flex: 1,
            minHeight: 0,
            overflow: 'hidden',
        }, children: [_jsxs(Paper, { variant: "outlined", sx: {
                    width: 256,
                    flexShrink: 0,
                    alignSelf: 'stretch',
                    borderRadius: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                }, children: [_jsxs(Box, { sx: {
                            p: 1.25,
                            borderBottom: `1px solid ${colorTokens.borderLight}`,
                            display: 'flex',
                            gap: 1,
                            flexShrink: 0,
                        }, children: [_jsx(AppInput, { placeholder: t('profileUsers.searchPlaceholder'), value: search, onChange: (e) => setSearch(e.target.value), sx: { flex: 1 }, slotProps: {
                                    input: {
                                        startAdornment: (_jsx(InputAdornment, { position: "start", children: _jsx(SearchIcon, { sx: { fontSize: 14, color: colorTokens.darkGrey } }) })),
                                    },
                                } }), _jsx(Tooltip, { title: t('profileUsers.addUserTooltip'), children: _jsxs(Button, { variant: "contained", size: "small", onClick: () => {
                                        setAddPayload({
                                            role: 'technician',
                                            assignedModules: [],
                                            assignedLocations: [],
                                            countryCode: 'IN',
                                        });
                                        setAddError(null);
                                        setAddFieldErrors({});
                                        // setOpenModules([])
                                        setIsAddMode(true);
                                    }, sx: {
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.5,
                                        px: 0.75,
                                        py: 0.75,
                                        bgcolor: colorTokens.primary,
                                        borderRadius: 2,
                                        fontSize: 11,
                                        fontWeight: 700,
                                        textTransform: 'none',
                                        minHeight: 'auto',
                                        lineHeight: 1,
                                    }, children: [_jsx(AddIcon, { sx: { fontSize: 14 } }), t('profileUsers.addButton')] }) })] }), _jsx(Box, { sx: {
                            px: 1.5,
                            pt: 1.25,
                            pb: 0.5,
                            borderBottom: `1px solid ${colorTokens.borderLight}`,
                            flexShrink: 0,
                        }, children: _jsx(Typography, { sx: {
                                fontSize: 9,
                                fontWeight: 700,
                                letterSpacing: '0.06em',
                                color: colorTokens.darkGrey,
                                textTransform: 'uppercase',
                            }, children: t('profileUsers.farmUsers') }) }), _jsxs(Box, { sx: { flex: 1, overflowY: 'auto', minHeight: 0 }, children: [selfProfile && showSelf && (_jsxs(Box, { onClick: () => handleSelect('self'), sx: {
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1.25,
                                    px: 1.5,
                                    py: 1.25,
                                    cursor: 'pointer',
                                    borderBottom: `1px solid ${colorTokens.borderLight}`,
                                    borderLeft: isSelf ? `3px solid ${colorTokens.magenta}` : '3px solid transparent',
                                    bgcolor: isSelf ? `${colorTokens.magenta}08` : 'white',
                                    '&:hover': {
                                        bgcolor: isSelf ? `${colorTokens.magenta}08` : `${colorTokens.lightGrey}`,
                                    },
                                    transition: 'background 0.15s',
                                }, children: [_jsx(Avatar, { first: selfProfile.firstName ?? selfProfile.first_name ?? '', last: selfProfile.lastName ?? selfProfile.last_name ?? '', gradient: `linear-gradient(135deg, ${colorTokens.primary}dd, ${colorTokens.magenta}bb)`, size: 32, src: selfProfile.profilePhotoUrl ?? undefined }), _jsxs(Box, { sx: { flex: 1, minWidth: 0 }, children: [_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 0.75 }, children: [_jsxs(Typography, { sx: {
                                                            fontSize: 11,
                                                            fontWeight: 600,
                                                            color: isSelf ? colorTokens.primary : '#1a2233',
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            whiteSpace: 'nowrap',
                                                        }, children: [selfProfile.firstName ?? selfProfile.first_name, ' ', selfProfile.lastName ?? selfProfile.last_name] }), _jsx(Box, { component: "span", sx: {
                                                            px: 0.75,
                                                            py: 0.25,
                                                            borderRadius: 0.75,
                                                            fontSize: 8,
                                                            fontWeight: 700,
                                                            bgcolor: `${colorTokens.magenta}15`,
                                                            color: colorTokens.magenta,
                                                            flexShrink: 0,
                                                        }, children: t('profileUsers.youBadge') })] }), _jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.25 }, children: [_jsx(RoleChip, { role: selfProfile.userType ?? selfProfile.user_type ?? 'owner' }), _jsx(Box, { sx: {
                                                            width: 6,
                                                            height: 6,
                                                            borderRadius: '50%',
                                                            bgcolor: colorTokens.green,
                                                            flexShrink: 0,
                                                        } }), _jsx(Typography, { sx: { fontSize: 9, color: colorTokens.darkGrey }, children: t('profileUsers.activeStatus') })] })] })] })), loadingUsers ? (_jsx(Box, { sx: { display: 'flex', justifyContent: 'center', py: 2 }, children: _jsx(CircularProgress, { size: 18 }) })) : (_jsxs(_Fragment, { children: [adminUsers.map((u) => {
                                        const isSelected = !isAddMode && selected !== 'self' && selected.id === u.id;
                                        const isActive = u.status === 'active';
                                        return (_jsxs(Box, { onClick: () => handleSelect(u), sx: {
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1.25,
                                                px: 1.5,
                                                py: 1.25,
                                                cursor: 'pointer',
                                                borderBottom: `1px solid ${colorTokens.borderLight}`,
                                                borderLeft: isSelected
                                                    ? `3px solid ${colorTokens.magenta}`
                                                    : '3px solid transparent',
                                                bgcolor: isSelected ? `${colorTokens.magenta}08` : 'white',
                                                '&:hover': {
                                                    bgcolor: isSelected ? `${colorTokens.magenta}08` : colorTokens.lightGrey,
                                                },
                                                transition: 'background 0.15s',
                                            }, children: [_jsx(Avatar, { first: u.firstName, last: u.lastName, gradient: isActive
                                                        ? `linear-gradient(135deg, ${colorTokens.primary}dd, ${colorTokens.magenta}bb)`
                                                        : `linear-gradient(135deg, #ccc, #aaa)`, size: 32 }), _jsxs(Box, { sx: { flex: 1, minWidth: 0 }, children: [_jsx(Typography, { sx: {
                                                                fontSize: 11,
                                                                fontWeight: 600,
                                                                color: isSelected ? colorTokens.primary : colorTokens.textDark,
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis',
                                                                whiteSpace: 'nowrap',
                                                            }, children: u.fullName }), _jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.25 }, children: [_jsx(RoleChip, { role: u.role }), _jsx(Box, { sx: {
                                                                        width: 6,
                                                                        height: 6,
                                                                        borderRadius: '50%',
                                                                        bgcolor: isActive ? colorTokens.green : colorTokens.darkGrey,
                                                                        flexShrink: 0,
                                                                    } }), _jsx(Typography, { sx: { fontSize: 9, color: colorTokens.darkGrey }, children: u.status === 'active'
                                                                        ? t('profileUsers.activeStatus')
                                                                        : t('profileUsers.inactiveStatus') })] })] })] }, u.id));
                                    }), groupedUsers.map((group) => (_jsxs(Box, { children: [_jsx(Box, { sx: {
                                                    px: 1.5,
                                                    pt: 1.1,
                                                    pb: 0.4,
                                                    borderBottom: `1px solid ${colorTokens.borderLight}`,
                                                }, children: _jsx(Typography, { sx: {
                                                        fontSize: 9,
                                                        fontWeight: 700,
                                                        letterSpacing: '0.06em',
                                                        color: colorTokens.secondary,
                                                        textTransform: 'uppercase',
                                                    }, children: roleLabel(group.role) }) }), group.users.map((u) => {
                                                const isSelected = !isAddMode && selected !== 'self' && selected.id === u.id;
                                                const isActive = u.status === 'active';
                                                return (_jsxs(Box, { onClick: () => handleSelect(u), sx: {
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 1.25,
                                                        px: 1.5,
                                                        py: 1.25,
                                                        cursor: 'pointer',
                                                        borderBottom: `1px solid ${colorTokens.borderLight}`,
                                                        borderLeft: isSelected
                                                            ? `3px solid ${colorTokens.magenta}`
                                                            : '3px solid transparent',
                                                        bgcolor: isSelected ? `${colorTokens.magenta}08` : 'white',
                                                        '&:hover': {
                                                            bgcolor: isSelected
                                                                ? `${colorTokens.magenta}08`
                                                                : colorTokens.lightGrey,
                                                        },
                                                        transition: 'background 0.15s',
                                                    }, children: [_jsx(Avatar, { first: u.firstName, last: u.lastName, gradient: isActive
                                                                ? `linear-gradient(135deg, ${colorTokens.primary}dd, ${colorTokens.magenta}bb)`
                                                                : `linear-gradient(135deg, #ccc, #aaa)`, size: 32 }), _jsxs(Box, { sx: { flex: 1, minWidth: 0 }, children: [_jsx(Typography, { sx: {
                                                                        fontSize: 11,
                                                                        fontWeight: 600,
                                                                        color: isSelected ? colorTokens.primary : colorTokens.textDark,
                                                                        overflow: 'hidden',
                                                                        textOverflow: 'ellipsis',
                                                                        whiteSpace: 'nowrap',
                                                                    }, children: u.fullName }), _jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.25 }, children: [_jsx(RoleChip, { role: u.role }), _jsx(Box, { sx: {
                                                                                width: 6,
                                                                                height: 6,
                                                                                borderRadius: '50%',
                                                                                bgcolor: isActive ? colorTokens.green : colorTokens.darkGrey,
                                                                                flexShrink: 0,
                                                                            } }), _jsx(Typography, { sx: { fontSize: 9, color: colorTokens.darkGrey }, children: u.status === 'active'
                                                                                ? t('profileUsers.activeStatus')
                                                                                : t('profileUsers.inactiveStatus') })] })] })] }, u.id));
                                            })] }, group.role)))] })), filteredSkretting.length > 0 && (_jsxs(_Fragment, { children: [_jsx(Box, { sx: {
                                            px: 1.5,
                                            pt: 1.25,
                                            pb: 0.5,
                                            borderBottom: `1px solid ${colorTokens.borderLight}`,
                                        }, children: _jsx(Typography, { sx: {
                                                fontSize: 9,
                                                fontWeight: 700,
                                                letterSpacing: '0.06em',
                                                color: colorTokens.secondary,
                                                textTransform: 'uppercase',
                                            }, children: t('profileUsers.skrettingGroup') }) }), filteredSkretting.map((u) => {
                                        const isActive = u.status === 'active';
                                        return (_jsxs(Box, { sx: {
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1.25,
                                                px: 1.5,
                                                py: 1.25,
                                                borderBottom: `1px solid ${colorTokens.borderLight}`,
                                                bgcolor: 'white',
                                                '&:last-child': { borderBottom: 'none' },
                                            }, children: [_jsx(Avatar, { first: u.firstName, last: u.lastName, gradient: `linear-gradient(135deg, ${colorTokens.secondary}dd, ${colorTokens.primary}bb)`, size: 32 }), _jsxs(Box, { sx: { flex: 1, minWidth: 0 }, children: [_jsx(Typography, { sx: {
                                                                fontSize: 11,
                                                                fontWeight: 600,
                                                                color: '#1a2233',
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis',
                                                                whiteSpace: 'nowrap',
                                                            }, children: u.fullName }), _jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.25 }, children: [_jsx(RoleChip, { role: "account_manager" }), _jsx(Box, { sx: {
                                                                        width: 6,
                                                                        height: 6,
                                                                        borderRadius: '50%',
                                                                        bgcolor: isActive ? colorTokens.green : colorTokens.darkGrey,
                                                                        flexShrink: 0,
                                                                    } }), _jsx(Typography, { sx: { fontSize: 9, color: colorTokens.darkGrey }, children: isActive
                                                                        ? t('profileUsers.activeStatus')
                                                                        : t('profileUsers.inactiveStatus') })] })] })] }, `skretting-${u.id}`));
                                    })] }))] })] }), _jsxs(Box, { sx: {
                    flex: 1,
                    minWidth: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1.5,
                    overflowY: 'auto',
                    alignSelf: 'stretch',
                    pb: 2,
                }, children: [_jsxs(Drawer, { anchor: "right", open: isAddMode, onClose: handleCancelAdd, PaperProps: {
                            sx: {
                                width: '30vw',
                                minWidth: 360,
                                display: 'flex',
                                flexDirection: 'column',
                            },
                        }, children: [_jsxs(Box, { sx: {
                                    px: 2.5,
                                    py: 2,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    borderBottom: `1px solid ${colorTokens.borderLight}`,
                                    flexShrink: 0,
                                }, children: [_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 1.5 }, children: [_jsx("input", { ref: addAvatarInputRef, type: "file", accept: "image/jpeg,image/png,image/webp", style: { display: 'none' }, onChange: handleAddAvatarChange }), _jsxs(Box, { onClick: () => addAvatarInputRef.current?.click(), sx: {
                                                    position: 'relative',
                                                    cursor: 'pointer',
                                                    flexShrink: 0,
                                                    '&:hover .add-avatar-overlay': { opacity: 1 },
                                                }, children: [_jsx(Avatar, { first: addPayload.firstName ?? '', last: addPayload.lastName ?? '', gradient: `linear-gradient(135deg, ${colorTokens.primary}dd, ${colorTokens.magenta}bb)`, size: 44, src: addAvatarPreview ?? undefined }), _jsx(Box, { className: "add-avatar-overlay", sx: {
                                                            position: 'absolute',
                                                            inset: 0,
                                                            borderRadius: '50%',
                                                            bgcolor: 'rgba(0,0,0,0.45)',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            opacity: 0,
                                                            transition: 'opacity 0.2s',
                                                        }, children: addAvatarUploading ? (_jsx(CircularProgress, { size: 16, sx: { color: 'white' } })) : (_jsx(CameraAltOutlinedIcon, { sx: { color: 'white', fontSize: 16 } })) })] }), _jsxs(Box, { children: [_jsx(Typography, { sx: { fontWeight: 700, color: '#1a2233', fontSize: 14 }, children: addPayload.firstName || addPayload.lastName
                                                            ? `${addPayload.firstName ?? ''} ${addPayload.lastName ?? ''}`.trim()
                                                            : t('profileUsers.addNewUser') }), _jsx(Typography, { sx: { fontSize: 11, color: colorTokens.darkGrey }, children: t('profileUsers.addUserSubtitle') })] })] }), _jsx(IconButton, { size: "small", onClick: handleCancelAdd, sx: { color: colorTokens.darkGrey }, children: _jsx(CloseIcon, { sx: { fontSize: 18 } }) })] }), _jsxs(Box, { sx: { flex: 1, overflowY: 'auto', px: 2.5, py: 2 }, children: [addError && (_jsx(Alert, { severity: "error", sx: {
                                            mb: 2,
                                            fontSize: 12,
                                            fontWeight: 600,
                                            backgroundColor: colorTokens.errorBg,
                                            border: `1px solid ${colorTokens.errorBorder}`,
                                            boxShadow: `inset 4px 0 0 ${colorTokens.error}`,
                                            '& .MuiAlert-icon': { color: colorTokens.error },
                                            '& .MuiAlert-action .MuiIconButton-root': { color: colorTokens.error },
                                        }, onClose: () => setAddError(null), children: addError })), _jsx(Box, { sx: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mb: 1.5 }, children: ['firstName', 'lastName'].map((key) => (_jsxs(Box, { children: [_jsx(Typography, { sx: { fontSize: 11, fontWeight: 600, color: colorTokens.primary, mb: 0.75 }, children: key === 'firstName' ? t('profileUsers.firstName') : t('profileUsers.lastName') }), _jsx(AppInput, { value: addPayload[key] ?? '', error: !!addFieldErrors[key], onChange: (e) => {
                                                        setAddPayload((p) => ({ ...p, [key]: e.target.value }));
                                                        if (addFieldErrors[key])
                                                            setAddFieldErrors((prev) => ({ ...prev, [key]: undefined }));
                                                    } }), addFieldErrors[key] && (_jsx(Typography, { sx: { fontSize: 10, color: colorTokens.error, mt: 0.25, ml: 0.25 }, children: addFieldErrors[key] }))] }, key))) }), _jsxs(Box, { sx: { mb: 1.5 }, children: [_jsxs(Typography, { sx: { fontSize: 11, fontWeight: 600, color: colorTokens.primary, mb: 0.75 }, children: [t('profileUsers.email'), ' ', _jsx(Box, { component: "span", sx: { color: colorTokens.error }, children: "*" })] }), _jsx(AppInput, { type: "email", value: addPayload.email ?? '', error: !!addFieldErrors.email, onChange: (e) => {
                                                    setAddPayload((p) => ({ ...p, email: e.target.value }));
                                                    if (addFieldErrors.email)
                                                        setAddFieldErrors((prev) => ({ ...prev, email: undefined }));
                                                } }), addFieldErrors.email && (_jsx(Typography, { sx: { fontSize: 10, color: colorTokens.error, mt: 0.25, ml: 0.25 }, children: addFieldErrors.email }))] }), _jsxs(Box, { sx: { mb: 1.5 }, children: [_jsxs(Typography, { sx: { fontSize: 11, fontWeight: 600, color: colorTokens.primary, mb: 0.75 }, children: [t('profileUsers.phone'), ' ', _jsx(Box, { component: "span", sx: { color: colorTokens.error }, children: "*" })] }), _jsx(AppInput, { value: addPayload.phone ?? '', error: !!addFieldErrors.phone, onChange: (e) => {
                                                    setAddPayload((p) => ({ ...p, phone: e.target.value }));
                                                    if (addFieldErrors.phone)
                                                        setAddFieldErrors((prev) => ({ ...prev, phone: undefined }));
                                                } }), addFieldErrors.phone && (_jsx(Typography, { sx: { fontSize: 10, color: colorTokens.error, mt: 0.25, ml: 0.25 }, children: addFieldErrors.phone }))] }), _jsxs(Box, { sx: { mb: 1.5 }, children: [_jsx(Typography, { sx: { fontSize: 11, fontWeight: 600, color: colorTokens.primary, mb: 0.75 }, children: t('profileUsers.role') }), _jsx(AppSelect, { value: addPayload.role ?? 'technician', onChange: (v) => handleAddRoleChange(v), options: EDIT_SUB_ROLES.map((r) => ({ value: r, label: roleLabel(r) })) })] }), requiresLocations((addPayload.role ?? 'technician')) && (_jsxs(Box, { sx: { mb: 1.5 }, children: [_jsx(Typography, { sx: { fontSize: 11, fontWeight: 600, color: colorTokens.primary, mb: 0.75 }, children: t('profileUsers.assignedLocations') }), locations.length === 0 ? (_jsx(Typography, { sx: { fontSize: 11, color: colorTokens.darkGrey }, children: t('profileUsers.noneAssigned') })) : (_jsx(Box, { sx: { display: 'flex', flexWrap: 'wrap', gap: 0.75 }, children: locations.map((location) => {
                                                    const isLocSelected = (addPayload.assignedLocations ?? []).includes(location.locationId);
                                                    return (_jsx(Chip, { label: location.name, size: "small", onClick: () => {
                                                            setAddPayload((prev) => {
                                                                const locs = prev.assignedLocations ?? [];
                                                                const exists = locs.includes(location.locationId);
                                                                return {
                                                                    ...prev,
                                                                    assignedLocations: exists
                                                                        ? locs.filter((id) => id !== location.locationId)
                                                                        : [...locs, location.locationId],
                                                                };
                                                            });
                                                        }, sx: {
                                                            fontSize: 10,
                                                            height: 24,
                                                            cursor: 'pointer',
                                                            borderRadius: 999,
                                                            border: `1px solid ${isLocSelected ? `${colorTokens.primary}55` : colorTokens.borderLight}`,
                                                            bgcolor: isLocSelected ? `${colorTokens.primary}12` : 'white',
                                                            color: isLocSelected ? colorTokens.primary : colorTokens.darkGrey,
                                                            fontWeight: isLocSelected ? 700 : 500,
                                                        } }, location.locationId));
                                                }) }))] })), requiresModules((addPayload.role ?? 'technician')) && (_jsxs(Box, { sx: { mb: 1.5 }, children: [_jsx(Divider, { sx: { mb: 1.5 } }), _jsx(Typography, { sx: {
                                                    fontSize: 9,
                                                    fontWeight: 700,
                                                    letterSpacing: '0.06em',
                                                    color: colorTokens.darkGrey,
                                                    textTransform: 'uppercase',
                                                    mb: 1,
                                                }, children: t('profileUsers.modulePermissions') }), _jsxs(Paper, { variant: "outlined", sx: { borderRadius: 2, overflow: 'hidden' }, children: [_jsxs(Box, { sx: {
                                                            display: 'grid',
                                                            gridTemplateColumns: '1fr repeat(3, 72px)',
                                                            alignItems: 'center',
                                                            px: 1.5,
                                                            py: 0.5,
                                                            bgcolor: colorTokens.lightGrey,
                                                            borderBottom: `1px solid ${colorTokens.borderLight}`,
                                                        }, children: [_jsx(Typography, { sx: {
                                                                    fontSize: 10,
                                                                    fontWeight: 700,
                                                                    color: colorTokens.darkGrey,
                                                                    textTransform: 'uppercase',
                                                                    letterSpacing: '0.05em',
                                                                }, children: t('profileUsers.moduleColumn') }), PERMS.map((perm) => {
                                                                const allChecked = MODULE_DEFS.every((md) => {
                                                                    const m = (addPayload.assignedModules ?? []).find((x) => x.moduleKey === md.key);
                                                                    return m?.permissions[perm] === true;
                                                                });
                                                                const anyChecked = MODULE_DEFS.some((md) => {
                                                                    const m = (addPayload.assignedModules ?? []).find((x) => x.moduleKey === md.key);
                                                                    return m?.permissions[perm] === true;
                                                                });
                                                                return (_jsxs(Box, { sx: {
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'flex-start',
                                                                        gap: 0.5,
                                                                    }, children: [_jsx(Checkbox, { size: "small", checked: allChecked, indeterminate: anyChecked && !allChecked, onChange: () => toggleAddColumnPerm(perm), sx: {
                                                                                p: 0.25,
                                                                                color: colorTokens.darkGrey,
                                                                                '&.Mui-checked, &.MuiCheckbox-indeterminate': {
                                                                                    color: colorTokens.primary,
                                                                                },
                                                                            } }), _jsx(Typography, { sx: {
                                                                                fontSize: 10,
                                                                                fontWeight: 700,
                                                                                color: colorTokens.darkGrey,
                                                                                textTransform: 'capitalize',
                                                                            }, children: t(`profileUsers.perm${perm.charAt(0).toUpperCase() + perm.slice(1)}`) })] }, perm));
                                                            })] }), MODULE_DEFS.map((mod) => {
                                                        const perms = getModulePermissions(addPayload.assignedModules, mod.key);
                                                        return (_jsxs(Box, { sx: {
                                                                display: 'grid',
                                                                gridTemplateColumns: '1fr repeat(3, 72px)',
                                                                alignItems: 'center',
                                                                px: 1.5,
                                                                borderBottom: `1px solid ${colorTokens.borderLight}`,
                                                                '&:last-child': { borderBottom: 'none' },
                                                                '&:hover': { bgcolor: '#f8fafc' },
                                                            }, children: [_jsx(Typography, { sx: { fontSize: 12, fontWeight: 500, color: '#1a2233' }, children: t(`profileUsers.${mod.i18nKey}`) }), PERMS.map((perm) => (_jsx(Box, { sx: { display: 'flex', justifyContent: 'flex-start' }, children: _jsx(Checkbox, { size: "small", checked: perms.includes(perm), onChange: () => toggleAddModulePerm(mod.key, perm), sx: {
                                                                            p: 0.5,
                                                                            color: colorTokens.darkGrey,
                                                                            '&.Mui-checked': { color: colorTokens.primary },
                                                                        } }) }, perm)))] }, mod.key));
                                                    })] })] }))] }), _jsx(Box, { sx: {
                                    px: 2.5,
                                    py: 2,
                                    borderTop: `1px solid ${colorTokens.borderLight}`,
                                    flexShrink: 0,
                                }, children: _jsx(Button, { fullWidth: true, variant: "contained", onClick: handleAddUser, disabled: addSaving, sx: {
                                        bgcolor: colorTokens.primary,
                                        borderRadius: 2,
                                        fontSize: 12,
                                        fontWeight: 700,
                                        minHeight: 40,
                                        textTransform: 'none',
                                        '&:hover': { bgcolor: '#002a50' },
                                    }, children: t('profileUsers.createUserButton') }) })] }), _jsxs(_Fragment, { children: [_jsx(Paper, { variant: "outlined", sx: { p: 2, borderRadius: 2 }, children: _jsxs(Box, { sx: {
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        gap: 2,
                                    }, children: [_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 1.5 }, children: [_jsx("input", { ref: avatarInputRef, type: "file", accept: "image/jpeg,image/png,image/webp", style: { display: 'none' }, onChange: handleAvatarChange }), _jsxs(Box, { onClick: isSelf ? handleAvatarClick : undefined, sx: {
                                                        position: 'relative',
                                                        cursor: isSelf ? 'pointer' : 'default',
                                                        '&:hover .avatar-overlay': isSelf ? { opacity: 1 } : {},
                                                    }, children: [_jsx(Avatar, { first: displayFirst, last: displayLast, gradient: `linear-gradient(135deg, ${colorTokens.primary}, ${colorTokens.magenta})`, size: 48, src: isSelf ? (selfProfile?.profilePhotoUrl ?? undefined) : undefined }), isSelf && (_jsx(Box, { className: "avatar-overlay", sx: {
                                                                position: 'absolute',
                                                                inset: 0,
                                                                borderRadius: '50%',
                                                                bgcolor: 'rgba(0,0,0,0.45)',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                opacity: 0,
                                                                transition: 'opacity 0.2s',
                                                            }, children: avatarUploading ? (_jsx(CircularProgress, { size: 18, sx: { color: 'white' } })) : (_jsx(CameraAltOutlinedIcon, { sx: { color: 'white', fontSize: 18 } })) }))] }), _jsxs(Box, { children: [_jsxs(Typography, { sx: { fontWeight: 700, color: '#1a2233', fontSize: 14 }, children: [displayFirst, " ", displayLast] }), _jsx(Typography, { sx: { fontSize: 11, color: colorTokens.darkGrey }, children: displayEmail }), _jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }, children: [_jsx(RoleChip, { role: displayRole }), _jsx(Chip, { label: displayStatus === 'active'
                                                                        ? t('profileUsers.activeStatus')
                                                                        : t('profileUsers.inactiveStatus'), size: "small", sx: {
                                                                        fontSize: 10,
                                                                        height: 20,
                                                                        fontWeight: 600,
                                                                        bgcolor: displayStatus === 'active'
                                                                            ? `${colorTokens.green}15`
                                                                            : `${colorTokens.darkGrey}12`,
                                                                        color: displayStatus === 'active' ? colorTokens.green : colorTokens.darkGrey,
                                                                    } })] })] })] }), _jsxs(Box, { sx: { display: 'flex', gap: 1, alignItems: 'center' }, children: [isSelf && (_jsxs(Box, { component: "span", sx: {
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        gap: 0.75,
                                                        px: 1.5,
                                                        py: 0.75,
                                                        borderRadius: '50px',
                                                        fontSize: 12,
                                                        lineHeight: 1,
                                                        fontWeight: 700,
                                                        bgcolor: `${colorTokens.magenta}10`,
                                                        color: colorTokens.magenta,
                                                        alignSelf: 'flex-start',
                                                        mt: 1.75,
                                                    }, children: [_jsx(AccountCircleOutlinedIcon, { sx: { fontSize: 12, color: colorTokens.magenta } }), t('profileUsers.yourProfileBadge')] })), !isSelf && selectedUser && (_jsxs(_Fragment, { children: [_jsx(Button, { size: "small", variant: "outlined", startIcon: selectedUser.status === 'active' ? (_jsx(BlockOutlinedIcon, {})) : (_jsx(CheckCircleOutlineIcon, {})), onClick: () => setStatusTarget(selectedUser), sx: {
                                                                borderColor: selectedUser.status === 'active' ? colorTokens.orange : colorTokens.green,
                                                                color: selectedUser.status === 'active' ? colorTokens.orange : colorTokens.green,
                                                                fontSize: 11,
                                                                borderRadius: 1.5,
                                                                '&:hover': {
                                                                    borderColor: selectedUser.status === 'active'
                                                                        ? colorTokens.orange
                                                                        : colorTokens.green,
                                                                    bgcolor: selectedUser.status === 'active'
                                                                        ? `${colorTokens.orange}08`
                                                                        : `${colorTokens.green}08`,
                                                                },
                                                            }, children: selectedUser.status === 'active'
                                                                ? t('profileUsers.inactivateButton')
                                                                : t('profileUsers.activateButton') }), _jsx(Button, { size: "small", variant: "outlined", startIcon: _jsx(DeleteOutlineIcon, {}), onClick: () => {
                                                                setDeleteTarget(selectedUser);
                                                                setDeleteText('');
                                                            }, sx: {
                                                                borderColor: colorTokens.error,
                                                                color: colorTokens.error,
                                                                fontSize: 11,
                                                                borderRadius: 1.5,
                                                                '&:hover': {
                                                                    borderColor: colorTokens.error,
                                                                    bgcolor: `${colorTokens.error}08`,
                                                                },
                                                            }, children: t('profileUsers.deleteButton') })] }))] })] }) }), _jsx(Collapse, { in: avatarSuccess, unmountOnExit: true, children: _jsx(Alert, { severity: "success", onClose: () => setAvatarSuccess(false), sx: {
                                        borderRadius: 2,
                                        fontSize: 12,
                                        fontWeight: 600,
                                    }, children: t('profileUsers.savedLabel') }) }), _jsx(Collapse, { in: !!avatarError, unmountOnExit: true, children: _jsx(Alert, { severity: avatarErrorSeverity, onClose: () => setAvatarError(null), sx: {
                                        borderRadius: 2,
                                        fontSize: 12,
                                        fontWeight: 600,
                                        backgroundColor: avatarErrorSeverity === 'warning'
                                            ? colorTokens.warningLight
                                            : colorTokens.errorBg,
                                        border: `1px solid ${avatarErrorSeverity === 'warning'
                                            ? `${colorTokens.warning}66`
                                            : colorTokens.errorBorder}`,
                                        boxShadow: `inset 4px 0 0 ${avatarErrorSeverity === 'warning' ? colorTokens.warning : colorTokens.error}`,
                                        '& .MuiAlert-icon': {
                                            color: avatarErrorSeverity === 'warning' ? colorTokens.warning : colorTokens.error,
                                        },
                                        '& .MuiAlert-action .MuiIconButton-root': {
                                            color: avatarErrorSeverity === 'warning' ? colorTokens.warning : colorTokens.error,
                                        },
                                    }, children: avatarError }) }), _jsxs(Box, { sx: {
                                    display: 'grid',
                                    gridTemplateColumns: '1fr 1fr',
                                    gap: 1.5,
                                    alignItems: 'start',
                                }, children: [_jsxs(Paper, { variant: "outlined", sx: { p: 2.5, borderRadius: 2 }, children: [profileError && (_jsx(Alert, { severity: profileErrorSeverity, sx: {
                                                    mb: 2,
                                                    fontSize: 12,
                                                    fontWeight: 600,
                                                    backgroundColor: profileErrorSeverity === 'warning'
                                                        ? colorTokens.warningLight
                                                        : colorTokens.errorBg,
                                                    border: `1px solid ${profileErrorSeverity === 'warning'
                                                        ? `${colorTokens.warning}66`
                                                        : colorTokens.errorBorder}`,
                                                    boxShadow: `inset 4px 0 0 ${profileErrorSeverity === 'warning' ? colorTokens.warning : colorTokens.error}`,
                                                    '& .MuiAlert-icon': {
                                                        color: profileErrorSeverity === 'warning'
                                                            ? colorTokens.warning
                                                            : colorTokens.error,
                                                    },
                                                    '& .MuiAlert-action .MuiIconButton-root': {
                                                        color: profileErrorSeverity === 'warning'
                                                            ? colorTokens.warning
                                                            : colorTokens.error,
                                                    },
                                                }, onClose: () => setProfileError(null), children: profileError })), _jsx(Typography, { sx: {
                                                    fontSize: 9,
                                                    fontWeight: 700,
                                                    letterSpacing: '0.06em',
                                                    color: colorTokens.darkGrey,
                                                    textTransform: 'uppercase',
                                                    mb: 1.5,
                                                }, children: t('profileUsers.profileDetails') }), _jsx(Box, { sx: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mb: 1.5 }, children: ['firstName', 'lastName'].map((key) => (_jsxs(Box, { children: [_jsx(Typography, { sx: { fontSize: 11, fontWeight: 600, color: colorTokens.primary, mb: 0.75 }, children: key === 'firstName'
                                                                ? t('profileUsers.firstName')
                                                                : t('profileUsers.lastName') }), _jsx(AppInput, { value: editState[key], onChange: (e) => setEditState((prev) => ({ ...prev, [key]: e.target.value })) })] }, key))) }), _jsxs(Box, { sx: { mb: 1.5 }, children: [_jsx(Typography, { sx: { fontSize: 11, fontWeight: 600, color: colorTokens.primary, mb: 0.75 }, children: t('profileUsers.email') }), _jsxs(Box, { sx: { display: 'flex', gap: 1, alignItems: 'center' }, children: [_jsxs(Box, { sx: {
                                                                    flex: 1,
                                                                    minHeight: 36,
                                                                    px: 1.25,
                                                                    py: 0.9,
                                                                    borderRadius: 1.5,
                                                                    border: `1px solid ${colorTokens.borderLight}`,
                                                                    bgcolor: '#f9fafb',
                                                                    color: colorTokens.darkGrey,
                                                                    fontSize: 12,
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'space-between',
                                                                }, children: [_jsx("span", { children: displayEmail || t('profileUsers.notSet') }), _jsx(Tooltip, { title: (isSelf ? selfProfile?.isEmailVerified : selectedUser?.isEmailVerified)
                                                                            ? t('profileUsers.verified')
                                                                            : t('profileUsers.notVerified'), children: (isSelf ? selfProfile?.isEmailVerified : selectedUser?.isEmailVerified) ? (_jsx(VerifiedIcon, { sx: { fontSize: 16, color: '#57e657', flexShrink: 0 } })) : (_jsx(NewReleasesIcon, { sx: { fontSize: 16, color: '#e53935', flexShrink: 0 } })) })] }), _jsx(Button, { size: "small", variant: "outlined", onClick: () => openOTPDialog('email', isSelf ? undefined : selectedUser.id), sx: {
                                                                    borderColor: colorTokens.magenta,
                                                                    color: colorTokens.magenta,
                                                                    fontSize: 10,
                                                                    borderRadius: 1.5,
                                                                    whiteSpace: 'nowrap',
                                                                    flexShrink: 0,
                                                                    '&:hover': { borderColor: colorTokens.magenta, bgcolor: 'transparent' },
                                                                }, children: t('profileUsers.changeButton') })] })] }), _jsxs(Box, { sx: { mb: 1.5 }, children: [_jsx(Typography, { sx: { fontSize: 11, fontWeight: 600, color: colorTokens.primary, mb: 0.75 }, children: t('profileUsers.phone') }), _jsxs(Box, { sx: { display: 'flex', gap: 1, alignItems: 'center' }, children: [_jsxs(Box, { sx: {
                                                                    flex: 1,
                                                                    minHeight: 36,
                                                                    px: 1.25,
                                                                    py: 0.9,
                                                                    borderRadius: 1.5,
                                                                    border: `1px solid ${colorTokens.borderLight}`,
                                                                    bgcolor: '#f9fafb',
                                                                    color: colorTokens.darkGrey,
                                                                    fontSize: 12,
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'space-between',
                                                                }, children: [_jsx("span", { children: isSelf
                                                                            ? (selfProfile?.phoneNumber ?? t('profileUsers.notSet'))
                                                                            : (selectedUser?.phone ?? t('profileUsers.notSet')) }), _jsx(Tooltip, { title: (isSelf ? selfProfile?.isPhoneVerified : selectedUser?.isPhoneVerified)
                                                                            ? t('profileUsers.verified')
                                                                            : t('profileUsers.notVerified'), children: (isSelf ? selfProfile?.isPhoneVerified : selectedUser?.isPhoneVerified) ? (_jsx(VerifiedIcon, { sx: { fontSize: 16, color: '#57e657', flexShrink: 0 } })) : (_jsx(NewReleasesIcon, { sx: { fontSize: 16, color: '#e53935', flexShrink: 0 } })) })] }), _jsx(Button, { size: "small", variant: "outlined", onClick: () => openOTPDialog('phone', isSelf ? undefined : selectedUser.id), sx: {
                                                                    borderColor: colorTokens.magenta,
                                                                    color: colorTokens.magenta,
                                                                    fontSize: 10,
                                                                    borderRadius: 1.5,
                                                                    whiteSpace: 'nowrap',
                                                                    flexShrink: 0,
                                                                    '&:hover': { borderColor: colorTokens.magenta, bgcolor: 'transparent' },
                                                                }, children: t('profileUsers.changeButton') })] }), _jsx(Typography, { sx: { fontSize: 10, color: colorTokens.darkGrey, mt: 0.5 }, children: t('profileUsers.otpRequiredHint') })] }), !isSelf && (_jsxs(Box, { sx: { mb: 1.5 }, children: [_jsx(Typography, { sx: { fontSize: 11, fontWeight: 600, color: colorTokens.primary, mb: 0.75 }, children: t('profileUsers.role') }), _jsx(AppSelect, { value: editState.role, onChange: (v) => handleEditRoleChange(v), options: availableSubRoles.map((r) => ({ value: r, label: roleLabel(r) })) })] })), _jsxs(Box, { sx: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mb: 1.5 }, children: [_jsxs(Box, { children: [_jsxs(Typography, { sx: {
                                                                    fontSize: 11,
                                                                    fontWeight: 600,
                                                                    color: colorTokens.primary,
                                                                    mb: 0.75,
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    gap: 0.5,
                                                                }, children: [_jsx(PublicOutlinedIcon, { sx: { fontSize: 13, color: colorTokens.darkGrey } }), t('profileUsers.country')] }), isSelf ? (_jsx(AppSelect, { value: editState.countryCode, onChange: (v) => {
                                                                    const nextCountryCode = String(v);
                                                                    setEditState((prev) => ({ ...prev, countryCode: nextCountryCode }));
                                                                    void syncTimezonesForCountry({
                                                                        countryInput: nextCountryCode,
                                                                        currentTimezone: editState.timezoneName,
                                                                        preserveExistingTimezone: false,
                                                                    });
                                                                }, disabled: loadingCountries, options: countries.map((country) => ({
                                                                    value: country.code,
                                                                    label: country.name,
                                                                })) })) : (_jsx(Box, { sx: {
                                                                    minHeight: 36,
                                                                    px: 1.25,
                                                                    py: 0.9,
                                                                    borderRadius: 1.5,
                                                                    border: `1px solid ${colorTokens.borderLight}`,
                                                                    bgcolor: '#f9fafb',
                                                                    color: colorTokens.darkGrey,
                                                                    fontSize: 12,
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                }, children: countries.find((c) => c.code === editState.countryCode)?.name ||
                                                                    editState.countryCode ||
                                                                    t('profileUsers.notSet') }))] }), _jsxs(Box, { children: [_jsxs(Typography, { sx: {
                                                                    fontSize: 11,
                                                                    fontWeight: 600,
                                                                    color: colorTokens.primary,
                                                                    mb: 0.75,
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    gap: 0.5,
                                                                }, children: [_jsx(AccessTimeOutlinedIcon, { sx: { fontSize: 13, color: colorTokens.darkGrey } }), t('profileUsers.timezone')] }), isSelf ? (_jsx(AppSelect, { value: editState.timezoneName, onChange: (v) => setEditState((prev) => ({ ...prev, timezoneName: String(v) })), disabled: loadingTimezones || timezones.length === 0, options: timezones.map((timezone) => ({
                                                                    value: timezone.name,
                                                                    label: timezone.name,
                                                                })) })) : (_jsx(Box, { sx: {
                                                                    minHeight: 36,
                                                                    px: 1.25,
                                                                    py: 0.9,
                                                                    borderRadius: 1.5,
                                                                    border: `1px solid ${colorTokens.borderLight}`,
                                                                    bgcolor: '#f9fafb',
                                                                    color: colorTokens.darkGrey,
                                                                    fontSize: 12,
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                }, children: editState.timezoneName || t('profileUsers.notSet') }))] })] }), (isManager || isWorkerOrTechnician) && (_jsxs(Box, { sx: { mb: 1.5 }, children: [_jsx(Typography, { sx: { fontSize: 11, fontWeight: 600, color: colorTokens.primary, mb: 0.75 }, children: t('profileUsers.assignedLocations') }), locations.length === 0 ? (_jsx(Typography, { sx: { fontSize: 11, color: colorTokens.darkGrey }, children: t('profileUsers.noneAssigned') })) : (_jsx(Box, { sx: { display: 'flex', flexWrap: 'wrap', gap: 0.75 }, children: locations.map((location) => {
                                                            const isSelected = editState.assignedLocations.includes(location.locationId);
                                                            return (_jsx(Chip, { label: location.name, size: "small", onClick: () => {
                                                                    setEditState((prev) => {
                                                                        const exists = prev.assignedLocations.includes(location.locationId);
                                                                        return {
                                                                            ...prev,
                                                                            assignedLocations: exists
                                                                                ? prev.assignedLocations.filter((id) => id !== location.locationId)
                                                                                : [...prev.assignedLocations, location.locationId],
                                                                        };
                                                                    });
                                                                }, sx: {
                                                                    fontSize: 10,
                                                                    height: 24,
                                                                    cursor: 'pointer',
                                                                    borderRadius: 999,
                                                                    border: `1px solid ${isSelected ? `${colorTokens.primary}55` : colorTokens.borderLight}`,
                                                                    bgcolor: isSelected ? `${colorTokens.primary}12` : 'white',
                                                                    color: isSelected ? colorTokens.primary : colorTokens.darkGrey,
                                                                    fontWeight: isSelected ? 700 : 500,
                                                                } }, location.locationId));
                                                        }) }))] })), _jsx(Button, { fullWidth: true, variant: "contained", onClick: handleSaveProfile, disabled: profileSaving, sx: {
                                                    bgcolor: profileSaveState === 'success'
                                                        ? colorTokens.green
                                                        : profileSaveState === 'error'
                                                            ? colorTokens.error
                                                            : colorTokens.primary,
                                                    borderRadius: 2,
                                                    fontSize: 12,
                                                    fontWeight: 700,
                                                    minHeight: 38,
                                                    textTransform: 'none',
                                                    '&:hover': {
                                                        bgcolor: profileSaveState === 'success'
                                                            ? colorTokens.green
                                                            : profileSaveState === 'error'
                                                                ? colorTokens.error
                                                                : '#002a50',
                                                    },
                                                }, children: profileSaved ? t('profileUsers.savedLabel') : t('profileUsers.saveProfile') })] }), _jsxs(Box, { sx: {
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: 1.5,
                                            ...(isSelf && { height: '100%', boxSizing: 'border-box' }),
                                        }, children: [_jsxs(Paper, { variant: "outlined", sx: { p: 2.5, borderRadius: 2, ...(isSelf && { flex: 1 }) }, children: [_jsx(Typography, { sx: {
                                                            fontSize: 9,
                                                            fontWeight: 700,
                                                            letterSpacing: '0.06em',
                                                            color: colorTokens.darkGrey,
                                                            textTransform: 'uppercase',
                                                            mb: 1.5,
                                                        }, children: isSelf ? t('profileUsers.changePassword') : t('profileUsers.changePassword') }), _jsxs(_Fragment, { children: [pwError && (_jsx(Alert, { severity: "error", sx: { mb: 1.5, fontSize: 12 }, onClose: () => setPwError(null), children: pwError })), _jsxs(Box, { sx: { mb: 1.5 }, children: [_jsx(Typography, { sx: { fontSize: 11, fontWeight: 600, color: colorTokens.primary, mb: 0.75 }, children: t('profileUsers.currentPassword') }), _jsx(AppInput, { type: showPw.current ? 'text' : 'password', value: pw.current, onChange: (e) => setPw((p) => ({ ...p, current: e.target.value })), slotProps: {
                                                                            input: {
                                                                                endAdornment: (_jsx(InputAdornment, { position: "end", children: _jsx(IconButton, { size: "small", onClick: () => setShowPw((p) => ({ ...p, current: !p.current })), edge: "end", children: showPw.current ? (_jsx(VisibilityOffOutlinedIcon, { sx: { fontSize: 16 } })) : (_jsx(VisibilityOutlinedIcon, { sx: { fontSize: 16 } })) }) })),
                                                                            },
                                                                        } })] }), _jsxs(Box, { sx: { mb: 1.5 }, children: [_jsx(Typography, { sx: { fontSize: 11, fontWeight: 600, color: colorTokens.primary, mb: 0.75 }, children: t('profileUsers.newPassword') }), _jsx(AppInput, { type: showPw.next ? 'text' : 'password', value: pw.next, onChange: (e) => setPw((p) => ({ ...p, next: e.target.value })), slotProps: {
                                                                            input: {
                                                                                endAdornment: (_jsx(InputAdornment, { position: "end", children: _jsx(IconButton, { size: "small", onClick: () => setShowPw((p) => ({ ...p, next: !p.next })), edge: "end", children: showPw.next ? (_jsx(VisibilityOffOutlinedIcon, { sx: { fontSize: 16 } })) : (_jsx(VisibilityOutlinedIcon, { sx: { fontSize: 16 } })) }) })),
                                                                            },
                                                                        } })] }), _jsxs(Box, { sx: { mb: 1.5 }, children: [_jsx(Typography, { sx: { fontSize: 11, fontWeight: 600, color: colorTokens.primary, mb: 0.75 }, children: t('profileUsers.confirmPassword') }), _jsx(AppInput, { type: showPw.confirm ? 'text' : 'password', value: pw.confirm, onChange: (e) => setPw((p) => ({ ...p, confirm: e.target.value })), sx: {
                                                                            ...(pw.confirm.length > 0
                                                                                ? {
                                                                                    '& .MuiOutlinedInput-notchedOutline': {
                                                                                        borderColor: `${pwMatch ? colorTokens.green : colorTokens.error} !important`,
                                                                                    },
                                                                                }
                                                                                : {}),
                                                                        }, slotProps: {
                                                                            input: {
                                                                                endAdornment: (_jsx(InputAdornment, { position: "end", children: _jsx(IconButton, { size: "small", onClick: () => setShowPw((p) => ({ ...p, confirm: !p.confirm })), edge: "end", children: showPw.confirm ? (_jsx(VisibilityOffOutlinedIcon, { sx: { fontSize: 16 } })) : (_jsx(VisibilityOutlinedIcon, { sx: { fontSize: 16 } })) }) })),
                                                                            },
                                                                        } })] }), _jsxs(Typography, { sx: {
                                                                    fontSize: 10,
                                                                    fontWeight: 700,
                                                                    color: colorTokens.darkGrey,
                                                                    mb: 0.75,
                                                                    mt: 0.5,
                                                                    letterSpacing: '0.04em',
                                                                    textTransform: 'uppercase',
                                                                }, children: [t('profileUsers.passwordGuidelines'), ":"] }), _jsx(PwChecklist, { value: pw.next }), pwSaved && (_jsxs(Box, { sx: {
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    gap: 1,
                                                                    px: 1.5,
                                                                    py: 1,
                                                                    borderRadius: 2,
                                                                    mt: 1.5,
                                                                    mb: 1.5,
                                                                    bgcolor: `${colorTokens.green}12`,
                                                                    border: `1px solid ${colorTokens.green}40`,
                                                                }, children: [_jsx(CheckIcon, { sx: { fontSize: 14, color: colorTokens.green, flexShrink: 0 } }), _jsx(Typography, { sx: { fontSize: 11, color: colorTokens.green, fontWeight: 600 }, children: t('profileUsers.passwordUpdated') })] })), _jsx(Button, { fullWidth: true, variant: "contained", onClick: handlePasswordChange, disabled: !pwCanSubmit || pwSaving, startIcon: pwSaving ? _jsx(CircularProgress, { size: 14, sx: { color: 'white' } }) : undefined, sx: {
                                                                    mt: 1.5,
                                                                    bgcolor: colorTokens.primary,
                                                                    borderRadius: 1.5,
                                                                    fontSize: 12,
                                                                    py: 1,
                                                                    '&:hover': { bgcolor: '#002a50' },
                                                                    opacity: pwCanSubmit ? 1 : 0.4,
                                                                    '&.Mui-disabled': {
                                                                        bgcolor: colorTokens.primary,
                                                                        color: 'white',
                                                                        opacity: 0.4,
                                                                    },
                                                                }, children: t('profileUsers.updatePassword') })] })] }), (isWorkerOrTechnician || (isSelf && false)) && (_jsxs(Paper, { variant: "outlined", sx: { borderRadius: 2, overflow: 'hidden' }, children: [_jsx(Box, { sx: {
                                                            px: 2.5,
                                                            py: 1.5,
                                                            borderBottom: `1px solid ${colorTokens.borderLight}`,
                                                        }, children: _jsx(Typography, { sx: {
                                                                fontSize: 9,
                                                                fontWeight: 700,
                                                                letterSpacing: '0.06em',
                                                                color: colorTokens.darkGrey,
                                                                textTransform: 'uppercase',
                                                            }, children: t('profileUsers.modulePermissions') }) }), _jsxs(Box, { sx: {
                                                            display: 'grid',
                                                            gridTemplateColumns: '1fr repeat(3, 72px)',
                                                            alignItems: 'center',
                                                            px: 1.5,
                                                            py: 0.5,
                                                            bgcolor: colorTokens.lightGrey,
                                                            borderBottom: `1px solid ${colorTokens.borderLight}`,
                                                        }, children: [_jsx(Typography, { sx: {
                                                                    fontSize: 10,
                                                                    fontWeight: 700,
                                                                    color: colorTokens.darkGrey,
                                                                    textTransform: 'uppercase',
                                                                    letterSpacing: '0.05em',
                                                                }, children: t('profileUsers.moduleColumn') }), PERMS.map((perm) => {
                                                                const allChecked = MODULE_DEFS.every((md) => {
                                                                    const m = editState.assignedModules.find((x) => x.moduleKey === md.key);
                                                                    return m?.permissions[perm] === true;
                                                                });
                                                                const anyChecked = MODULE_DEFS.some((md) => {
                                                                    const m = editState.assignedModules.find((x) => x.moduleKey === md.key);
                                                                    return m?.permissions[perm] === true;
                                                                });
                                                                return (_jsxs(Box, { sx: {
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'flex-start',
                                                                        gap: 0.5,
                                                                    }, children: [_jsx(Checkbox, { size: "small", checked: allChecked, indeterminate: anyChecked && !allChecked, onChange: () => toggleColumnPerm(perm), sx: {
                                                                                p: 0.25,
                                                                                color: colorTokens.darkGrey,
                                                                                '&.Mui-checked, &.MuiCheckbox-indeterminate': {
                                                                                    color: colorTokens.primary,
                                                                                },
                                                                            } }), _jsx(Typography, { sx: {
                                                                                fontSize: 10,
                                                                                fontWeight: 700,
                                                                                color: colorTokens.darkGrey,
                                                                                textTransform: 'capitalize',
                                                                            }, children: t(`profileUsers.perm${perm.charAt(0).toUpperCase() + perm.slice(1)}`) })] }, perm));
                                                            })] }), MODULE_DEFS.map((mod) => {
                                                        const perms = getModulePermissions(editState.assignedModules, mod.key);
                                                        return (_jsxs(Box, { sx: {
                                                                display: 'grid',
                                                                gridTemplateColumns: '1fr repeat(3, 72px)',
                                                                alignItems: 'center',
                                                                px: 1.5,
                                                                borderBottom: `1px solid ${colorTokens.borderLight}`,
                                                                '&:last-child': { borderBottom: 'none' },
                                                                '&:hover': { bgcolor: '#f8fafc' },
                                                            }, children: [_jsx(Typography, { sx: { fontSize: 12, fontWeight: 500, color: '#1a2233' }, children: t(`profileUsers.${mod.i18nKey}`) }), PERMS.map((perm) => (_jsx(Box, { sx: { display: 'flex', justifyContent: 'flex-start' }, children: _jsx(Checkbox, { size: "small", checked: perms.includes(perm), onChange: () => toggleModulePerm(mod.key, perm), sx: {
                                                                            p: 0.5,
                                                                            color: colorTokens.darkGrey,
                                                                            '&.Mui-checked': { color: colorTokens.primary },
                                                                        } }) }, perm)))] }, mod.key));
                                                    }), permError && (_jsx(Box, { sx: { px: 2.5, pt: 1.5 }, children: _jsx(Alert, { severity: "error", sx: {
                                                                fontSize: 12,
                                                                fontWeight: 600,
                                                                backgroundColor: colorTokens.errorBg,
                                                                border: `1px solid ${colorTokens.errorBorder}`,
                                                                boxShadow: `inset 4px 0 0 ${colorTokens.error}`,
                                                                '& .MuiAlert-icon': { color: colorTokens.error },
                                                                '& .MuiAlert-action .MuiIconButton-root': { color: colorTokens.error },
                                                            }, onClose: () => setPermError(null), children: permError }) })), _jsx(Box, { sx: { p: 2.5 }, children: _jsx(Button, { fullWidth: true, variant: "contained", onClick: handleSavePermissions, disabled: permSaving, sx: {
                                                                bgcolor: permSaveState === 'success'
                                                                    ? colorTokens.green
                                                                    : permSaveState === 'error'
                                                                        ? colorTokens.error
                                                                        : colorTokens.primary,
                                                                borderRadius: 2,
                                                                fontSize: 12,
                                                                fontWeight: 700,
                                                                minHeight: 38,
                                                                textTransform: 'none',
                                                                '&:hover': {
                                                                    bgcolor: permSaveState === 'success'
                                                                        ? colorTokens.green
                                                                        : permSaveState === 'error'
                                                                            ? colorTokens.error
                                                                            : '#002a50',
                                                                },
                                                            }, children: permSaved ? t('profileUsers.savedLabel') : t('profileUsers.savePermissions') }) })] }))] })] })] })] }), _jsxs(Dialog, { open: false, onClose: () => { }, maxWidth: "sm", fullWidth: true, PaperProps: { sx: { borderRadius: 1.5 } }, children: [_jsxs(DialogTitle, { sx: { fontWeight: 700, fontSize: 14, pb: 1, textTransform: 'none', pr: 5 }, children: [t('profileUsers.addNewUser'), _jsx(Typography, { sx: {
                                    fontSize: 11,
                                    color: colorTokens.darkGrey,
                                    fontWeight: 400,
                                    textTransform: 'none',
                                }, children: t('profileUsers.addUserSubtitle') }), _jsx(IconButton, { onClick: () => { }, children: _jsx(CloseIcon, { sx: { fontSize: 18 } }) })] }), _jsxs(DialogContent, { children: [addError && (_jsx(Alert, { severity: "error", onClose: () => setAddError(null), children: addError })), _jsx(Box, { sx: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mt: 1 }, children: ['firstName', 'lastName'].map((k) => (_jsxs(Box, { children: [_jsxs(Typography, { sx: { fontSize: 11, fontWeight: 600, color: colorTokens.primary, mb: 0.75 }, children: [k === 'firstName' ? t('profileUsers.firstName') : t('profileUsers.lastName'), " *"] }), _jsx(AppInput, { value: addPayload[k] ?? '', onChange: (e) => setAddPayload((p) => ({ ...p, [k]: e.target.value })) })] }, k))) }), _jsxs(Box, { sx: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mt: 1.5 }, children: [_jsxs(Box, { children: [_jsxs(Typography, { sx: { fontSize: 11, fontWeight: 600, color: colorTokens.primary, mb: 0.75 }, children: [t('profileUsers.email'), " *"] }), _jsx(AppInput, { type: "email", value: addPayload.email ?? '', onChange: (e) => setAddPayload((p) => ({ ...p, email: e.target.value })) })] }), _jsxs(Box, { children: [_jsxs(Typography, { sx: { fontSize: 11, fontWeight: 600, color: colorTokens.primary, mb: 0.75 }, children: [t('profileUsers.phone'), " *"] }), _jsx(AppInput, { type: "tel", value: addPayload.phone ?? '', onChange: (e) => setAddPayload((p) => ({ ...p, phone: e.target.value })) })] })] }), _jsxs(Box, { sx: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mt: 1.5 }, children: [_jsxs(Box, { children: [_jsxs(Typography, { sx: { fontSize: 11, fontWeight: 600, color: colorTokens.primary, mb: 0.75 }, children: [t('profileUsers.role'), " *"] }), _jsx(AppSelect, { value: addPayload.role ?? 'technician', onChange: (v) => handleAddRoleChange(v), options: EDIT_SUB_ROLES.map((r) => ({ value: r, label: roleLabel(r) })) })] }), _jsxs(Box, { children: [_jsx(Typography, { sx: { fontSize: 11, fontWeight: 600, color: colorTokens.primary, mb: 0.75 }, children: t('profileUsers.country') }), _jsx(AppSelect, { value: addPayload.countryCode ?? '', onChange: (v) => setAddPayload((p) => ({ ...p, countryCode: String(v) })), options: countries.map((c) => ({ value: c.code, label: c.name })) })] })] }), requiresLocations((addPayload.role ?? 'technician')) && (_jsxs(Box, { sx: { mt: 1.5 }, children: [_jsxs(Typography, { sx: { fontSize: 11, fontWeight: 600, color: colorTokens.primary, mb: 0.75 }, children: [t('profileUsers.assignedLocations'), " *"] }), _jsx(Box, { sx: { display: 'flex', flexWrap: 'wrap', gap: 0.75 }, children: locations.map((location) => {
                                            const isSelected = (addPayload.assignedLocations ?? []).includes(location.locationId);
                                            return (_jsx(Chip, { label: location.name, size: "small", onClick: () => {
                                                    setAddPayload((prev) => {
                                                        const selectedLocations = prev.assignedLocations ?? [];
                                                        const exists = selectedLocations.includes(location.locationId);
                                                        return {
                                                            ...prev,
                                                            assignedLocations: exists
                                                                ? selectedLocations.filter((id) => id !== location.locationId)
                                                                : [...selectedLocations, location.locationId],
                                                        };
                                                    });
                                                }, sx: {
                                                    fontSize: 10,
                                                    height: 24,
                                                    cursor: 'pointer',
                                                    borderRadius: 999,
                                                    border: `1px solid ${isSelected ? `${colorTokens.primary}55` : colorTokens.borderLight}`,
                                                    bgcolor: isSelected ? `${colorTokens.primary}12` : 'white',
                                                    color: isSelected ? colorTokens.primary : colorTokens.darkGrey,
                                                    fontWeight: isSelected ? 700 : 500,
                                                } }, location.locationId));
                                        }) })] })), requiresModules((addPayload.role ?? 'technician')) && (_jsxs(Box, { sx: { mt: 1.5 }, children: [_jsxs(Typography, { sx: { fontSize: 11, fontWeight: 600, color: colorTokens.primary, mb: 1 }, children: [t('profileUsers.modulePermissions'), " *"] }), _jsxs(Box, { sx: {
                                            border: `1px solid ${colorTokens.borderLight}`,
                                            borderRadius: 1.5,
                                            overflow: 'hidden',
                                        }, children: [_jsxs(Box, { sx: {
                                                    display: 'grid',
                                                    gridTemplateColumns: '1fr repeat(3, 72px)',
                                                    px: 1.5,
                                                    py: 0.75,
                                                    bgcolor: colorTokens.lightGrey,
                                                    borderBottom: `1px solid ${colorTokens.borderLight}`,
                                                }, children: [_jsx(Typography, { sx: {
                                                            fontSize: 10,
                                                            fontWeight: 700,
                                                            color: colorTokens.darkGrey,
                                                            textTransform: 'uppercase',
                                                            letterSpacing: '0.05em',
                                                        }, children: t('profileUsers.moduleColumn') }), PERMS.map((perm) => {
                                                        const allChecked = MODULE_DEFS.every((md) => {
                                                            const m = (addPayload.assignedModules ?? []).find((x) => x.moduleKey === md.key);
                                                            return m?.permissions[perm] === true;
                                                        });
                                                        const anyChecked = MODULE_DEFS.some((md) => {
                                                            const m = (addPayload.assignedModules ?? []).find((x) => x.moduleKey === md.key);
                                                            return m?.permissions[perm] === true;
                                                        });
                                                        return (_jsxs(Box, { sx: {
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'flex-start',
                                                                gap: 0.5,
                                                            }, children: [_jsx(Checkbox, { size: "small", checked: allChecked, indeterminate: anyChecked && !allChecked, onChange: () => toggleAddColumnPerm(perm), sx: {
                                                                        p: 0.25,
                                                                        color: colorTokens.darkGrey,
                                                                        '&.Mui-checked, &.MuiCheckbox-indeterminate': {
                                                                            color: colorTokens.primary,
                                                                        },
                                                                    } }), _jsx(Typography, { sx: {
                                                                        fontSize: 10,
                                                                        fontWeight: 700,
                                                                        color: colorTokens.darkGrey,
                                                                        textTransform: 'capitalize',
                                                                        pb: 0.5,
                                                                    }, children: t(`profileUsers.perm${perm.charAt(0).toUpperCase() + perm.slice(1)}`) })] }, perm));
                                                    })] }), MODULE_DEFS.map((moduleDef) => {
                                                const permissions = getModulePermissions(addPayload.assignedModules, moduleDef.key);
                                                return (_jsxs(Box, { sx: {
                                                        display: 'grid',
                                                        gridTemplateColumns: '1fr repeat(3, 72px)',
                                                        alignItems: 'center',
                                                        px: 1.5,
                                                        py: 0.9,
                                                        borderBottom: `1px solid ${colorTokens.borderLight}`,
                                                        '&:last-child': { borderBottom: 'none' },
                                                        '&:hover': { bgcolor: '#f8fafc' },
                                                    }, children: [_jsx(Typography, { sx: { fontSize: 11, fontWeight: 600, color: '#1a2233' }, children: t(`profileUsers.${moduleDef.i18nKey}`) }), PERMS.map((perm) => (_jsx(Box, { sx: { display: 'flex', justifyContent: 'flex-start' }, children: _jsx(Checkbox, { size: "small", checked: permissions.includes(perm), onChange: () => toggleAddModulePerm(moduleDef.key, perm), sx: {
                                                                    p: 0.5,
                                                                    color: colorTokens.darkGrey,
                                                                    '&.Mui-checked': { color: colorTokens.primary },
                                                                } }) }, perm)))] }, moduleDef.key));
                                            })] })] }))] }), _jsxs(DialogActions, { sx: { px: 3, pb: 2.5, gap: 1 }, children: [_jsx(Button, { variant: "outlined", size: "small", onClick: () => { }, children: t('profileUsers.cancelButton') }), _jsx(Button, { variant: "contained", color: "primary", onClick: handleAddUser, disabled: addSaving ||
                                    !addPayload.firstName ||
                                    !addPayload.lastName ||
                                    !addPayload.email ||
                                    !addPayload.phone ||
                                    (requiresLocations((addPayload.role ?? 'technician')) &&
                                        (addPayload.assignedLocations?.length ?? 0) === 0) ||
                                    (requiresModules((addPayload.role ?? 'technician')) &&
                                        !hasAnyModulePermission(addPayload.assignedModules ?? [])), startIcon: addSaving ? _jsx(CircularProgress, { size: 14, color: "inherit" }) : undefined, children: t('profileUsers.createUserButton') })] })] }), _jsx(Snackbar, { open: saveToast.open, autoHideDuration: 2800, anchorOrigin: { vertical: 'top', horizontal: 'right' }, onClose: () => setSaveToast({ open: false, message: '', severity: 'success' }), children: _jsx(Alert, { severity: saveToast.severity, onClose: () => setSaveToast({ open: false, message: '', severity: 'success' }), children: saveToast.message }) }), _jsxs(Dialog, { open: !!deleteTarget, onClose: () => setDeleteTarget(null), maxWidth: "xs", fullWidth: true, PaperProps: { sx: { borderRadius: 1.5 } }, children: [_jsx(DialogTitle, { sx: { fontWeight: 700, fontSize: 14, color: colorTokens.error }, children: t('profileUsers.deleteAccountTitle') }), _jsxs(DialogContent, { children: [_jsx(Typography, { sx: { fontSize: 12, color: colorTokens.darkGrey, lineHeight: 1.6, mb: 2 }, children: t('profileUsers.deleteAccountDesc', {
                                    name: `${deleteTarget?.firstName ?? ''} ${deleteTarget?.lastName ?? ''}`.trim(),
                                }) }), _jsx(Typography, { sx: { fontSize: 11, fontWeight: 600, color: colorTokens.primary, mb: 0.75 }, children: t('profileUsers.deleteConfirmInstruction', {
                                    word: t('profileUsers.deleteConfirmWord'),
                                }) }), _jsx(AppInput, { placeholder: t('profileUsers.deleteConfirmWord'), value: deleteText, onChange: (e) => setDeleteText(e.target.value) })] }), _jsxs(DialogActions, { sx: { px: 3, pb: 2.5, gap: 1 }, children: [_jsx(Button, { variant: "outlined", size: "small", onClick: () => setDeleteTarget(null), children: t('profileUsers.cancelButton') }), _jsx(Button, { variant: "contained", color: "error", onClick: handleDeleteConfirm, disabled: deleteLoading || deleteText !== t('profileUsers.deleteConfirmWord'), startIcon: deleteLoading ? _jsx(CircularProgress, { size: 14, color: "inherit" }) : undefined, children: t('profileUsers.deleteAccountTitle') })] })] }), _jsxs(Dialog, { open: !!statusTarget, onClose: () => setStatusTarget(null), maxWidth: "xs", fullWidth: true, PaperProps: { sx: { borderRadius: 1.5 } }, children: [_jsx(DialogTitle, { sx: { fontWeight: 700, fontSize: 14 }, children: statusTarget?.status === 'active'
                            ? t('profileUsers.inactivateTitle')
                            : t('profileUsers.activateTitle') }), _jsx(DialogContent, { children: _jsx(Typography, { sx: { fontSize: 12, color: colorTokens.darkGrey, lineHeight: 1.6 }, children: statusTarget?.status === 'active'
                                ? t('profileUsers.inactivateDesc', {
                                    name: `${statusTarget?.firstName ?? ''} ${statusTarget?.lastName ?? ''}`.trim(),
                                })
                                : t('profileUsers.activateDesc', {
                                    name: `${statusTarget?.firstName ?? ''} ${statusTarget?.lastName ?? ''}`.trim(),
                                }) }) }), _jsxs(DialogActions, { sx: { px: 3, pb: 2.5, gap: 1 }, children: [_jsx(Button, { variant: "outlined", size: "small", onClick: () => setStatusTarget(null), children: t('profileUsers.cancelButton') }), _jsx(Button, { variant: "contained", color: statusTarget?.status === 'active' ? 'error' : 'primary', onClick: handleStatusConfirm, disabled: statusLoading, startIcon: statusLoading ? _jsx(CircularProgress, { size: 14, color: "inherit" }) : undefined, children: statusTarget?.status === 'active'
                                    ? t('profileUsers.inactivateButton')
                                    : t('profileUsers.activateButton') })] })] }), _jsx(OTPVerificationDialog, { open: otpDialogOpen, onClose: () => setOtpDialogOpen(false), type: otpDialogType, userId: otpDialogUserId, currentValue: otpCurrentValue, isIndia: otpIsIndia, onSuccess: handleOTPSuccess })] }));
});
ProfileUsers.displayName = 'ProfileUsers';
